import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { X, Calendar, Clock, Bell, Users, MapPin, Palette, Shield, Zap, ChevronRight, Check, GitBranch, DollarSign, FileText, Wrench, Smartphone, CalendarDays, Trello, ClipboardList, FileSearch, Package, ShoppingCart, MessageSquare, BarChart3, Mail, CreditCard, Unlink, Receipt, Trash2, Plus, AlertTriangle, Globe, Pin, SlidersHorizontal, Copy, ZapOff, Star, Image, Sparkles, Target, ScanLine } from 'lucide-react';
import * as Icons from 'lucide-react';
import ServiceAreasManagementModal from './ServiceAreasManagementModal';
import { InstalledApp } from '../hooks/useAppSystem';
import { usePowerUps } from '../hooks/usePowerUps';
import { useBusiness } from '../contexts/BusinessContext';
import { useSubscription } from '../contexts/SubscriptionContext';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import AnalyticsSettings from './settings/AnalyticsSettings';
import TwilioSettings from './settings/TwilioSettings';
import GmailSettings from './settings/GmailSettings';
import StripeConnectButton from './StripeConnectButton';
import GoogleAdsConnectButton from './GoogleAdsConnectButton';
import CustomizeFeedbackFormModal from './CustomizeFeedbackFormModal';
import CustomizeLiabilityWaiverModal from './CustomizeLiabilityWaiverModal';
import LiveIntakeFormsConfigureModal from './crm/LiveIntakeFormsConfigureModal';
import CrmCustomFieldsModal from './crm/CrmCustomFieldsModal';
import BrandedIntakeHostSection from './crm/BrandedIntakeHostSection';
import BrandedProposalHostSection from './proposals/BrandedProposalHostSection';
import BrandedFeedbackHostSection from './feedback/BrandedFeedbackHostSection';
import { getFullUrl } from '../lib/siteConfig';
import PayrollPeriodSettingsForm from './payroll/PayrollSettingsModal';
import {
  DEFAULT_NO_ACTIVITY_HOURS,
  MAX_NO_ACTIVITY_HOURS,
  MIN_NO_ACTIVITY_HOURS,
  NO_ACTIVITY_ALERT_ENABLED_KEY,
  NO_ACTIVITY_HOURS_KEY,
  parseNoActivityHours,
} from '../lib/crm/noActivitySettings';

interface AppSettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  app: InstalledApp | null;
}

export default function AppSettingsModal({ isOpen, onClose, app }: AppSettingsModalProps) {
  const { selectedBusiness, forceLoadBusinesses } = useBusiness();
  const { subscription } = useSubscription();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'settings' | 'powerups'>('settings');
  const [enabledPowerUps, setEnabledPowerUps] = useState<string[]>([]);
  const [currentPlanPowerUps, setCurrentPlanPowerUps] = useState<string[]>([]);
  const [showLocationBookingInFAB, setShowLocationBookingInFAB] = useState(true); // Default to true
  const [showMobileBookingInFAB, setShowMobileBookingInFAB] = useState(true); // Default to true
  const [showTasksInFAB, setShowTasksInFAB] = useState(false);
  const [documentsFabEnabled, setDocumentsFabEnabled] = useState(false);
  const [docsSettingSaving, setDocsSettingSaving] = useState(false);
  const [showFieldQuotesInFAB, setShowFieldQuotesInFAB] = useState(false);
  const [createFieldQuotesFromBookings, setCreateFieldQuotesFromBookings] = useState(false);
  const [mapViewEnabled, setMapViewEnabled] = useState(false);
  const [salesPinsEnabled, setSalesPinsEnabled] = useState(true);
  const [currentBookingsHelperEnabled, setCurrentBookingsHelperEnabled] = useState(false);
  const [autoJobTrackingEnabled, setAutoJobTrackingEnabled] = useState(false); // Default to false
  const [autoJobTrackingRangeKm, setAutoJobTrackingRangeKm] = useState(2); // Default to 2 km
  const [bookingsByServiceAreaEnabled, setBookingsByServiceAreaEnabled] = useState(true); // Default to true
  const [showProposalInFAB, setShowProposalInFAB] = useState(true); // Default to true
  const [googleEarthMeasurementsEnabled, setGoogleEarthMeasurementsEnabled] = useState(false);
  const [attachPdfInEmail, setAttachPdfInEmail] = useState(true);
  const [publicProposalView, setPublicProposalView] = useState<'standard' | 'partial' | 'open'>('standard');
  const [thankYouMessageEnabled, setThankYouMessageEnabled] = useState(false);
  const [thankYouMessage, setThankYouMessage] = useState('');
  const [savedThankYouMessage, setSavedThankYouMessage] = useState('');
  const [thankYouMessageSaving, setThankYouMessageSaving] = useState(false);
  const [customSmsMessageEnabled, setCustomSmsMessageEnabled] = useState(false);
  const [customSmsMessage, setCustomSmsMessage] = useState('');
  const [savedCustomSmsMessage, setSavedCustomSmsMessage] = useState('');
  const [customSmsMessageSaving, setCustomSmsMessageSaving] = useState(false);
  const [customEmailMessageEnabled, setCustomEmailMessageEnabled] = useState(false);
  const [customEmailMessage, setCustomEmailMessage] = useState('');
  const [savedCustomEmailMessage, setSavedCustomEmailMessage] = useState('');
  const [customEmailMessageSaving, setCustomEmailMessageSaving] = useState(false);
  const [googleReviewsBadgeEnabled, setGoogleReviewsBadgeEnabled] = useState(false);
  const [googleReviewsRating, setGoogleReviewsRating] = useState(5);
  const [googleReviewsCount, setGoogleReviewsCount] = useState<number | null>(null);
  const [googleReviewsUrl, setGoogleReviewsUrl] = useState('');
  const [publicMessageEnabled, setPublicMessageEnabled] = useState(false);
  const [publicMessage, setPublicMessage] = useState('');
  const [savedPublicMessage, setSavedPublicMessage] = useState('');
  const [publicMessageSaving, setPublicMessageSaving] = useState(false);
  const [logoOnly, setLogoOnly] = useState(false);
  const [logoSize, setLogoSize] = useState<'small' | 'medium' | 'large'>('medium');
  const [showProjectInFAB, setShowProjectInFAB] = useState(true); // Default to true
  const [showWithdrawStockInFAB, setShowWithdrawStockInFAB] = useState(true); // Default to true
  const [defaultCalendarView, setDefaultCalendarView] = useState<'month' | 'week' | 'day'>('month'); // Default to month
  const [showInventoryInBookings, setShowInventoryInBookings] = useState(true); // Default to true
  const [showMaterialsInBookings, setShowMaterialsInBookings] = useState(true); // Default to true
  const [pinOrdersWidget, setPinOrdersWidget] = useState(false); // Default to false
  // What the pinned widget shows: purchase orders and/or customer orders
  const [pinShowPos, setPinShowPos] = useState(true);
  const [pinShowOrders, setPinShowOrders] = useState(false);
  const [aiPoDataEnabled, setAiPoDataEnabled] = useState(false); // Default to false — AI fills PO prices/pickup from supplier replies
  const [showFlatRateInBookings, setShowFlatRateInBookings] = useState(true); // Default to true
  const [showClockInFromFAB, setShowClockInFromFAB] = useState(true); // Default to true
  const [clockInUrlEnabled, setClockInUrlEnabled] = useState(false); // Default to false
  const [createExpensesFromFAB, setCreateExpensesFromFAB] = useState(false); // Default to false
  const [autoMatchPOs, setAutoMatchPOs] = useState(false); // Default to false
  
  // Subscriptions setting for Services app
  const [enableSubscriptions, setEnableSubscriptions] = useState(false);
  // Compare-at pricing setting for Services app (default off)
  const [compareAtPricing, setCompareAtPricing] = useState(false);
  const [mobileBufferTime, setMobileBufferTime] = useState(0);
  const [mobileBufferTimeLoaded, setMobileBufferTimeLoaded] = useState(false);
  // True once all calendar settings have finished their initial load (avoids toggles
  // flashing default states before their real values arrive).
  const [calendarSettingsLoaded, setCalendarSettingsLoaded] = useState(false);
  const [webAnalyticsEnabled, setWebAnalyticsEnabled] = useState(false); // Default to false
  const [showAnalyticsModal, setShowAnalyticsModal] = useState(false);
  const [settingsLoading, setSettingsLoading] = useState(false);
  
  // Twilio settings for Messages app
  const [twilioEnabled, setTwilioEnabled] = useState(false);
  const [twilioSettings, setTwilioSettings] = useState<any>(null);
  const [showTwilioModal, setShowTwilioModal] = useState(false);
  // Call recording & transcription (Messages app) — enable requires compliance acceptance
  const [callRecordingEnabled, setCallRecordingEnabled] = useState(false);
  const [callRecordingMode, setCallRecordingMode] = useState<'transcribe_only' | 'transcribe_store'>('transcribe_only');
  const [pendingRecordingMode, setPendingRecordingMode] = useState<'transcribe_only' | 'transcribe_store'>('transcribe_only');
  const [callRecordingSaving, setCallRecordingSaving] = useState(false);
  const [showRecordingComplianceModal, setShowRecordingComplianceModal] = useState(false);
  // Automatic "this call may be recorded" caller notice (inbound calls)
  const [callRecordingNoticeEnabled, setCallRecordingNoticeEnabled] = useState(true);
  const [showNoticeWarningModal, setShowNoticeWarningModal] = useState(false);
  
  // Gmail settings for Messages app
  const [gmailEnabled, setGmailEnabled] = useState(false);
  const [gmailConnections, setGmailConnections] = useState<any[]>([]);
  const [showGmailModal, setShowGmailModal] = useState(false);
  // Email signature (Messages app, when Gmail integration is active)
  const [emailSignature, setEmailSignature] = useState('');
  const [savedEmailSignature, setSavedEmailSignature] = useState('');
  const [emailSignatureSaving, setEmailSignatureSaving] = useState(false);
  // Message Organization setting
  const [messageOrganization, setMessageOrganization] = useState<'chronological' | 'sms_first' | 'email_first'>('chronological');
  const [messageOrganizationSaving, setMessageOrganizationSaving] = useState(false);
  
  // Stripe settings for Invoices app
  const [stripeConnected, setStripeConnected] = useState(false);
  const [stripeAccountId, setStripeAccountId] = useState<string | null>(null);
  
  // Feedback settings for Calendar apps (shared between calendar and mobile-calendar)
  const [feedbackEnabled, setFeedbackEnabled] = useState(true);
  const [isCustomizeFeedbackModalOpen, setIsCustomizeFeedbackModalOpen] = useState(false);
  // Disclosure for the branded/custom feedback URL sub-setting (defaults on when a hostname already exists)
  const [showBrandedFeedbackUrl, setShowBrandedFeedbackUrl] = useState(false);
  
  // Liability Waiver settings for Calendar apps (shared between calendar and mobile-calendar)
  const [liabilityWaiverEnabled, setLiabilityWaiverEnabled] = useState(false);
  const [isCustomizeLiabilityWaiverModalOpen, setIsCustomizeLiabilityWaiverModalOpen] = useState(false);
  
  // Teams settings
  const [assetsEnabled, setAssetsEnabled] = useState(false);

  // CRM Lost Reason settings
  const [crmRecordLostReason, setCrmRecordLostReason] = useState(true);
  const [crmLostReasons, setCrmLostReasons] = useState<Array<{ id: string; reason: string }>>([]);
  const [newLostReason, setNewLostReason] = useState('');
  const [crmLostReasonLoading, setCrmLostReasonLoading] = useState(false);
  const [crmLiveIntakeForms, setCrmLiveIntakeForms] = useState(false);
  const [crmCreateOpportunityFromFab, setCrmCreateOpportunityFromFab] = useState(false);
  const [crmAiCreateFromCard, setCrmAiCreateFromCard] = useState(false);
  const [crmCardDefaultSourceEnabled, setCrmCardDefaultSourceEnabled] = useState(false);
  const [crmCardDefaultSource, setCrmCardDefaultSource] = useState('');
  const [marketingCreateSalesPin, setMarketingCreateSalesPin] = useState(false);
  const [showLiveIntakeConfigureModal, setShowLiveIntakeConfigureModal] = useState(false);
  const [crmNoActivityEnabled, setCrmNoActivityEnabled] = useState(false);
  const [crmNoActivityHours, setCrmNoActivityHours] = useState(48);
  const [crmCustomFieldsEnabled, setCrmCustomFieldsEnabled] = useState(false);
  const [showCrmCustomFieldsModal, setShowCrmCustomFieldsModal] = useState(false);

  // Service Areas Management Modal
  const [showServiceAreasModal, setShowServiceAreasModal] = useState(false);
  
  // Check for navigation state to auto-open service areas modal
  useEffect(() => {
    const handleOpenServiceAreas = (event: CustomEvent) => {
      if (event.detail?.openServiceAreas && app?.app.slug === 'mobile-calendar') {
        setShowServiceAreasModal(true);
      }
    };
    
    window.addEventListener('openServiceAreas', handleOpenServiceAreas as EventListener);
    return () => {
      window.removeEventListener('openServiceAreas', handleOpenServiceAreas as EventListener);
    };
  }, [app?.app.slug]);
  
  // Auto-open service areas modal if modal was opened with that intent
  useEffect(() => {
    if (isOpen && app?.app.slug === 'mobile-calendar') {
      // Check if there's a pending service areas open request
      const pendingOpen = (window as any).__pendingServiceAreasOpen;
      if (pendingOpen) {
        setShowServiceAreasModal(true);
        (window as any).__pendingServiceAreasOpen = false;
      }
    }
  }, [isOpen, app?.app.slug]);

  // Reopen Customize Feedback after a Google sign-in that started there.
  useEffect(() => {
    if (!isOpen) return;
    const slug = app?.app.slug;
    if (slug !== 'calendar' && slug !== 'mobile-calendar') return;
    if (!(window as any).__pendingFeedbackFormOpen) return;
    setIsCustomizeFeedbackModalOpen(true);
    (window as any).__pendingFeedbackFormOpen = false;
  }, [isOpen, app?.app.slug]);
  
  const { powerUps, loading: powerUpsLoading, isPowerUpEnabled, togglePowerUp: togglePowerUpBackend, refetch } = usePowerUps(app?.app.slug || '');

  // App type checks
  const isCalendarApp = app?.app.slug === 'calendar' || app?.app.slug === 'mobile-calendar';
  const isServicesApp = app?.app.slug === 'services';
  const isTeamsApp = app?.app.slug === 'teams';
  const isAutomationsApp = app?.app.slug === 'automations';
  const isProposalsApp = app?.app.slug === 'proposals';
  const isProjectsApp = app?.app.slug === 'projects';
  const isInventoryApp = app?.app.slug === 'inventory';
  const isInvoicesApp = app?.app.slug === 'invoices';
  const isProductsApp = app?.app.slug === 'products';
  const isPayrollApp = app?.app.slug === 'payroll';
  const isCRMApp = app?.app.slug === 'crm';
  const isMarketingApp = app?.app.slug === 'marketing';
  const isAnalyticsApp = app?.app.slug === 'analytics';
  const isMessagesApp = app?.app.slug === 'messages';
  const isExpensesApp = app?.app.slug === 'expenses';
  const isDocsApp = app?.app.slug === 'tasks';
  const isCustomersApp = app?.app.slug === 'customers';

  useEffect(() => {
    if (!isOpen || !isDocsApp || !selectedBusiness?.id || !supabase) return;
    supabase
      .from('businesses')
      .select('documents_fab_enabled')
      .eq('id', selectedBusiness.id)
      .maybeSingle()
      .then(({ data }) => setDocumentsFabEnabled((data as any)?.documents_fab_enabled === true));
  }, [isOpen, isDocsApp, selectedBusiness?.id]);

  const saveDocumentsFabEnabled = async (next: boolean) => {
    if (!selectedBusiness?.id || !supabase) return;
    setDocsSettingSaving(true);
    setDocumentsFabEnabled(next);
    try {
      const { error } = await supabase.from('businesses').update({ documents_fab_enabled: next }).eq('id', selectedBusiness.id);
      if (error) throw error;
    } catch (e) {
      console.error('Failed to save documents setting:', e);
      setDocumentsFabEnabled(!next);
      toast.error('Failed to save setting');
    } finally {
      setDocsSettingSaving(false);
    }
  };

  // Load app settings when modal opens
  useEffect(() => {
    if (isOpen && app && selectedBusiness?.id) {
      if (app.app.slug === 'calendar') {
        setCalendarSettingsLoaded(false);
        void Promise.all([
          loadCalendarSettings(),
          loadMobileCalendarSettings(),
          loadFeedbackSettings(),
          loadLiabilityWaiverSettings()
        ]).finally(() => setCalendarSettingsLoaded(true));
      } else if (app.app.slug === 'mobile-calendar') {
        loadFeedbackSettings();
        loadLiabilityWaiverSettings();
        loadMobileCalendarSettings();
      } else if (app.app.slug === 'proposals') {
        loadProposalsSettings();
      } else if (app.app.slug === 'projects') {
        loadProjectsSettings();
      } else if (app.app.slug === 'inventory') {
        loadInventorySettings();
      } else if (app.app.slug === 'payroll') {
        loadPayrollSettings();
      } else if (app.app.slug === 'analytics') {
        loadAnalyticsSettings();
      } else if (app.app.slug === 'messages') {
        loadTwilioSettings();
        loadGmailSettings();
        loadMessagesEmailSignature();
        loadMessageOrganization();
      } else if (app.app.slug === 'invoices') {
        loadStripeSettings();
        loadInvoicesSettings();
      } else if (app.app.slug === 'expenses') {
        loadExpensesSettings();
      } else if (app.app.slug === 'services') {
        loadServicesSettings();
        loadStripeSettings();
      } else if (app.app.slug === 'crm') {
        loadCRMSettings();
      } else if (app.app.slug === 'marketing') {
        loadMarketingSettings();
      } else if (app.app.slug === 'teams') {
        loadTeamsSettings();
      }
    }
  }, [isOpen, app, selectedBusiness]);

  // Listen for Stripe Connect completion
  useEffect(() => {
    if (!isInvoicesApp) return;

    const handleStripeConnectSuccess = () => {
      // Reload Stripe settings when connection is successful
      setTimeout(() => {
        loadStripeSettings();
      }, 2000); // Give time for the webhook to process
    };

    // Listen for URL parameter changes indicating Stripe Connect return
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('stripe_connect') === 'success') {
      handleStripeConnectSuccess();
    }

    // Also listen for business context updates (which happen after Stripe Connect)
    const handleBusinessUpdate = () => {
      if (isOpen && isInvoicesApp) {
        loadStripeSettings();
      }
    };

    // Set up an interval to check for business updates
    const interval = setInterval(handleBusinessUpdate, 3000);

    return () => {
      clearInterval(interval);
    };
  }, [isOpen, isInvoicesApp, (selectedBusiness as any)?.stripe_connect_account_id]);

  const loadCalendarSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_location_booking_in_fab',
          p_default_value: true
        });

      if (!error && data !== null) {
        setShowLocationBookingInFAB(data);
      }

      const { data: tasksData, error: tasksError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_tasks_in_fab',
          p_default_value: false
        });

      if (!tasksError && tasksData !== null) {
        setShowTasksInFAB(tasksData);
      }

      const { data: fqFabData, error: fqFabError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_field_quotes_in_fab',
          p_default_value: false
        });

      if (!fqFabError && fqFabData !== null) {
        setShowFieldQuotesInFAB(!!fqFabData);
      } else {
        setShowFieldQuotesInFAB(false);
      }

      const { data: fqFromBookingData, error: fqFromBookingError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'create_field_quotes_from_bookings',
          p_default_value: false
        });

      if (!fqFromBookingError && fqFromBookingData !== null) {
        setCreateFieldQuotesFromBookings(!!fqFromBookingData);
      } else {
        setCreateFieldQuotesFromBookings(false);
      }
    } catch (error) {
      console.error('Error loading calendar settings:', error);
    }
  };

  const loadMobileCalendarSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Load mobile booking setting
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'show_mobile_booking_in_fab',
          p_default_value: true
        });

      if (!error && data !== null) {
        setShowMobileBookingInFAB(data);
      }

      // Load bookings by service area setting
      const { data: serviceAreaData, error: serviceAreaError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'mobile_booking_by_service_area',
          p_default_value: true
        });

      if (!serviceAreaError && serviceAreaData !== null) {
        setBookingsByServiceAreaEnabled(serviceAreaData);
      }
      
      // Load default calendar view setting
      const { data: viewData, error: viewError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'default_calendar_view',
          p_default_value: 'month'
        });

      if (!viewError && viewData !== null) {
        setDefaultCalendarView(viewData as 'month' | 'week' | 'day');
      }
      
      // Load map view setting
      const { data: mapViewData, error: mapViewError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'map_view_enabled',
          p_default_value: false
        });

      if (!mapViewError && mapViewData !== null) {
        setMapViewEnabled(mapViewData);
      }

      // Load sales pins setting
      const { data: salesPinsData, error: salesPinsError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'sales_pins_enabled',
          p_default_value: true
        });

      if (!salesPinsError && salesPinsData !== null) {
        setSalesPinsEnabled(salesPinsData);
      }
      
      // Load auto job tracking enabled setting
      const { data: autoTrackingEnabledData, error: autoTrackingEnabledError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'auto_job_tracking_enabled',
          p_default_value: false
        });

      if (!autoTrackingEnabledError && autoTrackingEnabledData !== null) {
        setAutoJobTrackingEnabled(autoTrackingEnabledData);
      }

      // Load auto job tracking range setting
      const { data: autoTrackingRangeData, error: autoTrackingRangeError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'auto_job_tracking_range_km',
          p_default_value: 2
        });

      if (!autoTrackingRangeError && autoTrackingRangeData !== null) {
        setAutoJobTrackingRangeKm(autoTrackingRangeData);
      }

      // Load current bookings helper setting
      const { data: bookingsHelperData, error: bookingsHelperError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'calendar',
          p_setting_key: 'current_bookings_helper_enabled',
          p_default_value: false
        });

      if (!bookingsHelperError && bookingsHelperData !== null) {
        setCurrentBookingsHelperEnabled(bookingsHelperData);
      }
      
      // Load buffer time from default online booking portal settings
      try {
        const { data: defPortal } = await supabase
          .from('online_booking_portals')
          .select('id')
          .eq('business_id', selectedBusiness.id)
          .eq('is_default', true)
          .maybeSingle();
        if (!defPortal?.id) {
          setMobileBufferTime(0);
        } else {
          const { data: bufferData, error: bufferError } = await supabase
            .from('online_booking_settings')
            .select('buffer_time')
            .eq('portal_id', defPortal.id)
            .single();

          if (bufferError) {
            if (bufferError.code === 'PGRST116') {
              setMobileBufferTime(0);
            } else {
              console.error('Error loading buffer time:', bufferError);
            }
          } else {
            setMobileBufferTime(bufferData?.buffer_time ?? 0);
          }
        }
      } finally {
        setMobileBufferTimeLoaded(true);
      }
    } catch (error) {
      console.error('Error loading mobile calendar settings:', error);
    }
  };

  const loadInvoicesSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      const [publicMessageEnabledResult, publicMessageResult] = await Promise.all([
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'public_message_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'public_message',
          p_default_value: ''
        }),
      ]);

      if (!publicMessageEnabledResult.error && publicMessageEnabledResult.data !== null) {
        setPublicMessageEnabled(!!publicMessageEnabledResult.data);
      } else {
        setPublicMessageEnabled(false);
      }
      if (!publicMessageResult.error && publicMessageResult.data !== null) {
        const msg = typeof publicMessageResult.data === 'string' ? publicMessageResult.data : '';
        setPublicMessage(msg);
        setSavedPublicMessage(msg);
      } else {
        setPublicMessage('');
        setSavedPublicMessage('');
      }

      const { data: subscriptionsData, error: subscriptionsError } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'services',
        p_setting_key: 'enable_subscriptions',
        p_default_value: false
      });
      if (!subscriptionsError && subscriptionsData !== null) {
        setEnableSubscriptions(!!subscriptionsData);
      }
    } catch (error) {
      console.error('Error loading invoices settings:', error);
    }
  };

  const loadProposalsSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      const [
        fabResult,
        earthResult,
        attachPdfResult,
        viewResult,
        thankYouEnabledResult,
        thankYouMessageResult,
        customSmsEnabledResult,
        customSmsMessageResult,
        customEmailEnabledResult,
        customEmailMessageResult,
        googleBadgeEnabledResult,
        googleRatingResult,
        googleCountResult,
        googleUrlResult,
        publicMessageEnabledResult,
        publicMessageResult,
        logoOnlyResult,
        logoSizeResult,
      ] = await Promise.all([
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_proposal_in_fab',
          p_default_value: true
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'google_earth_measurements_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'attach_pdf_in_email',
          p_default_value: true
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'public_proposal_view',
          p_default_value: 'standard'
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'thank_you_message_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'thank_you_message',
          p_default_value: ''
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'custom_sms_message_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'custom_sms_message',
          p_default_value: ''
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'custom_email_message_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'custom_email_message',
          p_default_value: ''
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'google_reviews_badge_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'google_reviews_rating',
          p_default_value: 5
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'google_reviews_count',
          p_default_value: null
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'google_reviews_url',
          p_default_value: ''
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'public_message_enabled',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'public_message',
          p_default_value: ''
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'logo_only',
          p_default_value: false
        }),
        supabase.rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'logo_size',
          p_default_value: 'medium'
        }),
      ]);

      if (!fabResult.error && fabResult.data !== null) {
        setShowProposalInFAB(fabResult.data);
      }
      if (!earthResult.error && earthResult.data !== null) {
        setGoogleEarthMeasurementsEnabled(earthResult.data);
      }
      if (!attachPdfResult.error && attachPdfResult.data !== null) {
        setAttachPdfInEmail(attachPdfResult.data);
      }
      if (!viewResult.error && (viewResult.data === 'standard' || viewResult.data === 'partial' || viewResult.data === 'open')) {
        setPublicProposalView(viewResult.data);
      }
      if (!thankYouEnabledResult.error && thankYouEnabledResult.data !== null) {
        setThankYouMessageEnabled(!!thankYouEnabledResult.data);
      }
      if (!thankYouMessageResult.error && thankYouMessageResult.data !== null) {
        const msg = typeof thankYouMessageResult.data === 'string' ? thankYouMessageResult.data : '';
        setThankYouMessage(msg);
        setSavedThankYouMessage(msg);
      }
      if (!customSmsEnabledResult.error && customSmsEnabledResult.data !== null) {
        setCustomSmsMessageEnabled(!!customSmsEnabledResult.data);
      }
      if (!customSmsMessageResult.error && customSmsMessageResult.data !== null) {
        const msg = typeof customSmsMessageResult.data === 'string' ? customSmsMessageResult.data : '';
        setCustomSmsMessage(msg);
        setSavedCustomSmsMessage(msg);
      }
      if (!customEmailEnabledResult.error && customEmailEnabledResult.data !== null) {
        setCustomEmailMessageEnabled(!!customEmailEnabledResult.data);
      }
      if (!customEmailMessageResult.error && customEmailMessageResult.data !== null) {
        const msg = typeof customEmailMessageResult.data === 'string' ? customEmailMessageResult.data : '';
        setCustomEmailMessage(msg);
        setSavedCustomEmailMessage(msg);
      }
      if (!googleBadgeEnabledResult.error && googleBadgeEnabledResult.data !== null) {
        setGoogleReviewsBadgeEnabled(!!googleBadgeEnabledResult.data);
      }
      if (!googleRatingResult.error && googleRatingResult.data !== null && googleRatingResult.data !== undefined) {
        const rating = Number(googleRatingResult.data);
        if (!Number.isNaN(rating)) setGoogleReviewsRating(Math.min(5, Math.max(0, rating)));
      }
      if (!googleCountResult.error) {
        if (googleCountResult.data === null || googleCountResult.data === undefined) {
          setGoogleReviewsCount(null);
        } else {
          const count = parseInt(String(googleCountResult.data), 10);
          setGoogleReviewsCount(Number.isNaN(count) ? null : Math.max(0, count));
        }
      }
      if (!googleUrlResult.error && googleUrlResult.data !== null) {
        setGoogleReviewsUrl(typeof googleUrlResult.data === 'string' ? googleUrlResult.data : '');
      }
      if (!publicMessageEnabledResult.error && publicMessageEnabledResult.data !== null) {
        setPublicMessageEnabled(!!publicMessageEnabledResult.data);
      }
      if (!publicMessageResult.error && publicMessageResult.data !== null) {
        const msg = typeof publicMessageResult.data === 'string' ? publicMessageResult.data : '';
        setPublicMessage(msg);
        setSavedPublicMessage(msg);
      }
      if (!logoOnlyResult.error && logoOnlyResult.data !== null) {
        setLogoOnly(!!logoOnlyResult.data);
      }
      if (!logoSizeResult.error && (logoSizeResult.data === 'small' || logoSizeResult.data === 'medium' || logoSizeResult.data === 'large')) {
        setLogoSize(logoSizeResult.data);
      }
    } catch (error) {
      console.error('Error loading proposals settings:', error);
    }
  };

  const loadProjectsSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_project_in_fab',
          p_default_value: true
        });

      if (!error && data !== null) {
        setShowProjectInFAB(data);
      }
    } catch (error) {
      console.error('Error loading projects settings:', error);
    }
  };

  const loadExpensesSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'create_expenses_from_fab',
          p_default_value: false
        });

      if (!error && data !== null) {
        setCreateExpensesFromFAB(data);
      }

      const { data: autoMatchData, error: autoMatchError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'auto_match_pos',
          p_default_value: false
        });

      if (!autoMatchError && autoMatchData !== null) {
        setAutoMatchPOs(autoMatchData);
      }
    } catch (error) {
      console.error('Error loading expenses settings:', error);
    }
  };

  const loadInventorySettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      // Load FAB setting
      const { data: fabData, error: fabError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_withdraw_stock_in_fab',
          p_default_value: true
        });

      if (!fabError && fabData !== null) {
        setShowWithdrawStockInFAB(fabData);
      }

      // Load booking inventory setting
      const { data: bookingData, error: bookingError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_inventory_in_bookings',
          p_default_value: true
        });

      if (!bookingError && bookingData !== null) {
        setShowInventoryInBookings(bookingData);
      }

      // Load materials setting
      const { data: materialsData, error: materialsError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_materials_in_bookings',
          p_default_value: true
        });

      if (!materialsError && materialsData !== null) {
        setShowMaterialsInBookings(materialsData);
      }

      // Load pin orders widget setting
      const { data: pinData, error: pinError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'pin_orders_widget',
          p_default_value: false
        });

      if (!pinError && pinData !== null) {
        setPinOrdersWidget(pinData);
      }

      // Pinned widget content: purchase orders (default on) / customer orders
      const { data: pinPosData, error: pinPosError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'pin_orders_show_pos',
          p_default_value: true
        });
      if (!pinPosError && pinPosData !== null) {
        setPinShowPos(pinPosData);
      }

      const { data: pinOrdersData, error: pinOrdersError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'pin_orders_show_orders',
          p_default_value: false
        });
      if (!pinOrdersError && pinOrdersData !== null) {
        setPinShowOrders(pinOrdersData);
      }

      // Load AI PO Data setting (off by default — uses AI tokens when on)
      const { data: aiPoData, error: aiPoError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'ai_po_data',
          p_default_value: false
        });

      if (!aiPoError && aiPoData !== null) {
        setAiPoDataEnabled(aiPoData);
      }
    } catch (error) {
      console.error('Error loading inventory settings:', error);
    }
  };

  const loadTeamsSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'teams',
          p_setting_key: 'assets_enabled',
          p_default_value: false
        });

      if (!error && data !== null) {
        setAssetsEnabled(data);
      }
    } catch (error) {
      console.error('Error loading teams settings:', error);
    }
  };

  const loadPayrollSettings = async () => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      // Load flat rate setting
      const { data: flatRateData, error: flatRateError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'show_flat_rate_in_bookings',
          p_default_value: true
        });

      if (!flatRateError && flatRateData !== null) {
        setShowFlatRateInBookings(flatRateData);
      }

      // Load clock in from FAB setting
      const { data: clockInData, error: clockInError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'clock_in_from_fab',
          p_default_value: true
        });

      if (!clockInError && clockInData !== null) {
        setShowClockInFromFAB(clockInData);
      }

      // Load clock in URL setting
      const { data: clockInUrlData, error: clockInUrlError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: 'clock_in_url_enabled',
          p_default_value: false
        });

      if (!clockInUrlError && clockInUrlData !== null) {
        setClockInUrlEnabled(clockInUrlData);
      }
    } catch (error) {
      console.error('Error loading payroll settings:', error);
    }
  };

  const loadAnalyticsSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Check if analytics is active by looking at the business_analytics_config table
      const { data, error } = await supabase
        .from('business_analytics_config')
        .select('is_active')
        .eq('business_id', selectedBusiness.id)
        .single();

      if (!error && data) {
        setWebAnalyticsEnabled(data.is_active || false);
      }
    } catch (error) {
      console.error('Error loading analytics settings:', error);
    }
  };

  const loadTwilioSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Load Twilio settings from the businesses table
      const { data, error } = await supabase
        .from('businesses')
        .select(`
          twilio_enabled,
          twilio_status,
          twilio_account_sid,
          twilio_auth_token,
          VITE_TWILIO_PHONE_NUMBER,
          twilio_call_forwarding_enabled,
          twilio_forwarding_number,
          twilio_webhooks_configured,
          twilio_application_sid,
          twilio_call_recording_enabled,
          twilio_call_recording_mode,
          twilio_call_recording_notice_enabled
        `)
        .eq('id', selectedBusiness.id)
        .single();

      if (!error && data) {
        setTwilioSettings(data);
        setTwilioEnabled(data.twilio_enabled || false);
        setCallRecordingEnabled((data as any).twilio_call_recording_enabled || false);
        const mode = (data as any).twilio_call_recording_mode === 'transcribe_store' ? 'transcribe_store' : 'transcribe_only';
        setCallRecordingMode(mode);
        setPendingRecordingMode(mode);
        setCallRecordingNoticeEnabled((data as any).twilio_call_recording_notice_enabled !== false);
      }
    } catch (error) {
      console.error('Error loading Twilio settings:', error);
    }
  };

  /** Toggle intent: off saves immediately; on requires accepting the compliance terms first. */
  const handleCallRecordingToggle = () => {
    if (callRecordingEnabled) {
      void saveCallRecordingEnabled(false);
    } else {
      setPendingRecordingMode(callRecordingMode);
      setShowRecordingComplianceModal(true);
    }
  };

  const saveCallRecordingEnabled = async (enabled: boolean, mode?: 'transcribe_only' | 'transcribe_store') => {
    if (!selectedBusiness?.id) return;
    setCallRecordingSaving(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          twilio_call_recording_enabled: enabled,
          ...(mode ? { twilio_call_recording_mode: mode } : {}),
          ...(enabled ? { twilio_call_recording_accepted_at: new Date().toISOString() } : {}),
        })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      setCallRecordingEnabled(enabled);
      if (mode) {
        setCallRecordingMode(mode);
      }
      setShowRecordingComplianceModal(false);
    } catch (error) {
      console.error('Error saving call recording setting:', error);
      alert('Failed to update call recording setting. Please try again.');
    } finally {
      setCallRecordingSaving(false);
    }
  };

  /** Caller-notice toggle: enabling saves immediately; disabling requires accepting a compliance warning. */
  const handleRecordingNoticeToggle = () => {
    if (callRecordingNoticeEnabled) {
      setShowNoticeWarningModal(true);
    } else {
      void saveRecordingNoticeEnabled(true);
    }
  };

  const saveRecordingNoticeEnabled = async (enabled: boolean) => {
    if (!selectedBusiness?.id) return;
    setCallRecordingSaving(true);
    try {
      const { error } = await supabase
        .from('businesses')
        .update({
          twilio_call_recording_notice_enabled: enabled,
          // Disabling the notice is a compliance-relevant choice — stamp acceptance
          ...(enabled ? {} : { twilio_call_recording_accepted_at: new Date().toISOString() }),
        })
        .eq('id', selectedBusiness.id);
      if (error) throw error;
      setCallRecordingNoticeEnabled(enabled);
      setShowNoticeWarningModal(false);
    } catch (error) {
      console.error('Error saving caller notice setting:', error);
      alert('Failed to update caller notice setting. Please try again.');
    } finally {
      setCallRecordingSaving(false);
    }
  };

  const loadGmailSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Load Gmail connections from the gmail_connections table
      const { data, error } = await supabase
        .from('gmail_connections')
        .select('*')
        .eq('business_id', selectedBusiness.id);

      if (!error && data) {
        setGmailConnections(data);
        setGmailEnabled(data.length > 0);
      }
    } catch (error) {
      console.error('Error loading Gmail settings:', error);
    }
  };

  const loadMessagesEmailSignature = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data, error } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'messages',
        p_setting_key: 'email_signature',
        p_default_value: '{}'
      });
      const body = (!error && data && typeof data === 'object' && data !== null && 'body' in data)
        ? String((data as { body?: string }).body ?? '')
        : '';
      setEmailSignature(body);
      setSavedEmailSignature(body);
    } catch (error) {
      console.error('Error loading email signature:', error);
      setEmailSignature('');
      setSavedEmailSignature('');
    }
  };

  const saveMessagesEmailSignature = async () => {
    if (!selectedBusiness?.id) return;
    try {
      setEmailSignatureSaving(true);
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'messages',
        p_setting_key: 'email_signature',
        p_setting_value: { body: emailSignature }
      });
      if (error) throw error;
      setSavedEmailSignature(emailSignature);
      toast.success('Email signature saved');
    } catch (error) {
      console.error('Error saving email signature:', error);
      toast.error('Failed to save email signature');
    } finally {
      setEmailSignatureSaving(false);
    }
  };

  const loadMessageOrganization = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data, error } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'messages',
        p_setting_key: 'message_organization',
        p_default_value: '{"value":"chronological"}'
      });
      const val = (!error && data && typeof data === 'object' && data !== null && 'value' in data)
        ? String((data as { value?: string }).value ?? 'chronological')
        : 'chronological';
      const valid = ['chronological', 'sms_first', 'email_first'].includes(val) ? val as 'chronological' | 'sms_first' | 'email_first' : 'chronological';
      setMessageOrganization(valid);
    } catch {
      setMessageOrganization('chronological');
    }
  };

  const saveMessageOrganization = async (value: 'chronological' | 'sms_first' | 'email_first') => {
    if (!selectedBusiness?.id) return;
    try {
      setMessageOrganizationSaving(true);
      setMessageOrganization(value);
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'messages',
        p_setting_key: 'message_organization',
        p_setting_value: { value }
      });
      if (error) throw error;
      toast.success('Message organization updated');
      window.dispatchEvent(new CustomEvent('message-organization-changed', { detail: { value } }));
    } catch {
      toast.error('Failed to save setting');
    } finally {
      setMessageOrganizationSaving(false);
    }
  };

  const loadStripeSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      // Load Stripe settings from the businesses table
      const { data, error } = await supabase
        .from('businesses')
        .select('stripe_connect_account_id, stripe_connect_onboarding_completed, stripe_connect_charges_enabled')
        .eq('id', selectedBusiness.id)
        .single();

      if (!error && data) {
        setStripeConnected(!!data.stripe_connect_account_id);
        setStripeAccountId(data.stripe_connect_account_id);
      }
    } catch (error) {
      console.error('Error loading Stripe settings:', error);
    }
  };

  const saveCalendarSetting = async (key: string, value: any) => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      setSettingsLoading(true);
      // Calendar-related settings should be saved to 'calendar' app slug
      const calendarSettings = [
        'show_mobile_booking_in_fab',
        'map_view_enabled',
        'sales_pins_enabled',
        'auto_job_tracking_enabled',
        'auto_job_tracking_range_km',
        'default_calendar_view',
        'show_tasks_in_fab',
        'show_field_quotes_in_fab',
        'create_field_quotes_from_bookings',
        'current_bookings_helper_enabled'
      ];
      const appSlug = calendarSettings.includes(key) ? 'calendar' : app.app.slug;
      
      const { error } = await supabase
        .rpc('set_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: appSlug,
          p_setting_key: key,
          p_setting_value: value
        });

      if (error) throw error;
      
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving calendar setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const saveExpensesSetting = async (key: string, value: any) => {
    if (!selectedBusiness?.id || !app?.app.slug) return;

    try {
      setSettingsLoading(true);
      
      const { error } = await supabase
        .rpc('set_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: app.app.slug,
          p_setting_key: key,
          p_setting_value: value
        });

      if (error) throw error;
      
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving expenses setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadServicesSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'services',
          p_setting_key: 'enable_subscriptions',
          p_default_value: false
        });

      if (!error && data !== null) {
        setEnableSubscriptions(data);
      }

      const { data: compareData, error: compareError } = await supabase
        .rpc('get_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'services',
          p_setting_key: 'compare_at_pricing',
          p_default_value: false
        });
      if (!compareError && compareData !== null) {
        setCompareAtPricing(compareData);
      }
    } catch (error) {
      console.error('Error loading services settings:', error);
    }
  };

  const saveServicesSetting = async (key: string, value: any) => {
    if (!selectedBusiness?.id) return;

    try {
      setSettingsLoading(true);
      
      const { error } = await supabase
        .rpc('set_app_setting', {
          p_business_id: selectedBusiness.id,
          p_app_slug: 'services',
          p_setting_key: key,
          p_setting_value: value
        });

      if (error) throw error;
      
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving services setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadCRMSettings = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'record_lost_reason',
        p_default_value: true
      });
      if (data !== null) setCrmRecordLostReason(data);

      const { data: liveIntake } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'live_intake_forms',
        p_default_value: false,
      });
      if (liveIntake !== null) setCrmLiveIntakeForms(Boolean(liveIntake));

      const { data: fabOpportunity } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'create_opportunity_from_fab',
        p_default_value: false,
      });
      if (fabOpportunity !== null) setCrmCreateOpportunityFromFab(Boolean(fabOpportunity));

      const { data: aiCardScan } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'ai_create_from_business_card',
        p_default_value: false,
      });
      if (aiCardScan !== null) setCrmAiCreateFromCard(Boolean(aiCardScan));

      const { data: cardSourceEnabled } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'business_card_default_source_enabled',
        p_default_value: false,
      });
      if (cardSourceEnabled !== null) setCrmCardDefaultSourceEnabled(Boolean(cardSourceEnabled));

      const { data: cardSource } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'business_card_default_source',
        p_default_value: '',
      });
      if (cardSource !== null) setCrmCardDefaultSource(String(cardSource));

      const { data: noActivityEnabled } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: NO_ACTIVITY_ALERT_ENABLED_KEY,
        p_default_value: false,
      });
      if (noActivityEnabled !== null) setCrmNoActivityEnabled(Boolean(noActivityEnabled));

      const { data: noActivityHours } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: NO_ACTIVITY_HOURS_KEY,
        p_default_value: DEFAULT_NO_ACTIVITY_HOURS,
      });
      if (noActivityHours !== null) {
        setCrmNoActivityHours(parseNoActivityHours(noActivityHours));
      }

      const { data: customFieldsEnabled } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: 'custom_fields_enabled',
        p_default_value: false,
      });
      if (customFieldsEnabled !== null) setCrmCustomFieldsEnabled(Boolean(customFieldsEnabled));

      const { data: reasons, error } = await supabase
        .from('crm_lost_reasons')
        .select('id, reason')
        .eq('business_id', selectedBusiness.id)
        .order('reason');
      if (!error) setCrmLostReasons(reasons || []);
    } catch (err) {
      console.error('Error loading CRM settings:', err);
    }
  };

  const saveCRMSetting = async (key: string, value: any) => {
    if (!selectedBusiness?.id) return;
    try {
      setSettingsLoading(true);
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'crm',
        p_setting_key: key,
        p_setting_value: value
      });
      if (error) throw error;
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving CRM setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadMarketingSettings = async () => {
    if (!selectedBusiness?.id) return;
    try {
      const { data: salesPin } = await supabase.rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'marketing',
        p_setting_key: 'create_sales_pin_from_fab',
        p_default_value: false,
      });
      if (salesPin !== null) setMarketingCreateSalesPin(Boolean(salesPin));
    } catch (err) {
      console.error('Error loading marketing settings:', err);
    }
  };

  const saveMarketingSetting = async (key: string, value: any) => {
    if (!selectedBusiness?.id) return;
    try {
      setSettingsLoading(true);
      const { error } = await supabase.rpc('set_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'marketing',
        p_setting_key: key,
        p_setting_value: value
      });
      if (error) throw error;
      toast.success('Setting saved successfully');
    } catch (error) {
      console.error('Error saving marketing setting:', error);
      toast.error('Failed to save setting');
    } finally {
      setSettingsLoading(false);
    }
  };

  const addLostReason = async () => {
    if (!selectedBusiness?.id || !newLostReason.trim()) return;
    setCrmLostReasonLoading(true);
    try {
      const { data, error } = await supabase
        .from('crm_lost_reasons')
        .insert({ business_id: selectedBusiness.id, reason: newLostReason.trim() })
        .select('id, reason')
        .single();
      if (error) {
        if (error.code === '23505') {
          toast.error('This reason already exists');
        } else {
          throw error;
        }
        return;
      }
      setCrmLostReasons(prev => [...prev, data].sort((a, b) => a.reason.localeCompare(b.reason)));
      setNewLostReason('');
      toast.success('Lost reason added');
    } catch (err) {
      console.error('Error adding lost reason:', err);
      toast.error('Failed to add lost reason');
    } finally {
      setCrmLostReasonLoading(false);
    }
  };

  const deleteLostReason = async (id: string) => {
    if (!selectedBusiness?.id) return;
    setCrmLostReasonLoading(true);
    try {
      const { error } = await supabase
        .from('crm_lost_reasons')
        .delete()
        .eq('id', id);
      if (error) throw error;
      setCrmLostReasons(prev => prev.filter(r => r.id !== id));
      toast.success('Lost reason removed');
    } catch (err) {
      console.error('Error deleting lost reason:', err);
      toast.error('Failed to remove lost reason');
    } finally {
      setCrmLostReasonLoading(false);
    }
  };

  const saveMobileBufferTime = async (minutes: number) => {
    if (!selectedBusiness?.id) return;

    try {
      setSettingsLoading(true);
      const { data: defPortal, error: pe } = await supabase
        .from('online_booking_portals')
        .select('id')
        .eq('business_id', selectedBusiness.id)
        .eq('is_default', true)
        .maybeSingle();
      if (pe || !defPortal?.id) {
        toast.error('No default online booking page found. Open Online Booking once to set it up.');
        return;
      }
      const { error } = await supabase
        .from('online_booking_settings')
        .upsert(
          { business_id: selectedBusiness.id, portal_id: defPortal.id, buffer_time: minutes },
          { onConflict: 'portal_id' }
        );

      if (error) throw error;
      toast.success('Buffer time updated');
    } catch (error) {
      console.error('Error saving buffer time:', error);
      toast.error('Failed to save buffer time');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Load feedback settings
  const loadFeedbackSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('feedback_enabled, feedback_custom_hostname')
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error loading feedback settings:', error);
        return;
      }

      const enabled = data?.feedback_enabled !== false; // Default to true if not set
      setFeedbackEnabled(enabled);
      setShowBrandedFeedbackUrl(Boolean(data?.feedback_custom_hostname));
    } catch (error) {
      console.error('Error loading feedback settings:', error);
    }
  };

  // Save feedback settings
  const saveFeedbackSettings = async (enabled: boolean) => {
    if (!selectedBusiness?.id) return;

    try {
      setSettingsLoading(true);
      const { error } = await supabase
        .from('businesses')
        .update({ feedback_enabled: enabled })
        .eq('id', selectedBusiness.id);

      if (error) throw error;

      setFeedbackEnabled(enabled);
      toast.success('Feedback settings saved successfully');
    } catch (error) {
      console.error('Error saving feedback settings:', error);
      toast.error('Failed to save feedback settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  // Load liability waiver settings
  const loadLiabilityWaiverSettings = async () => {
    if (!selectedBusiness?.id) return;

    try {
      const { data, error } = await supabase
        .from('businesses')
        .select('liability_waiver_enabled')
        .eq('id', selectedBusiness.id)
        .single();

      if (error) {
        console.error('Error loading liability waiver settings:', error);
        // If column doesn't exist or there's an error, default to false
        setLiabilityWaiverEnabled(false);
        return;
      }

      // Check if liability_waiver_enabled is explicitly true (not null, undefined, or false)
      const enabled = data?.liability_waiver_enabled === true;
      console.log('Loaded liability waiver settings:', { 
        enabled, 
        rawValue: data?.liability_waiver_enabled,
        businessId: selectedBusiness.id 
      });
      setLiabilityWaiverEnabled(enabled);
    } catch (error) {
      console.error('Error loading liability waiver settings:', error);
      setLiabilityWaiverEnabled(false);
    }
  };

  // Save liability waiver settings
  const saveLiabilityWaiverSettings = async (enabled: boolean) => {
    if (!selectedBusiness?.id) return;

    try {
      setSettingsLoading(true);
      const { error } = await supabase
        .from('businesses')
        .update({ liability_waiver_enabled: enabled })
        .eq('id', selectedBusiness.id);

      if (error) throw error;

      setLiabilityWaiverEnabled(enabled);
      toast.success('Liability waiver settings saved successfully');
    } catch (error) {
      console.error('Error saving liability waiver settings:', error);
      toast.error('Failed to save liability waiver settings');
    } finally {
      setSettingsLoading(false);
    }
  };

  useEffect(() => {
    if (app && powerUps) {
      // Initialize enabled power-ups from backend
      const enabled = Object.keys(powerUps).filter(key => powerUps[key]);
      setEnabledPowerUps(enabled);
    }
  }, [powerUps, app]);

  // Fetch current plan's included power-ups
  useEffect(() => {
    const fetchCurrentPlanPowerUps = async () => {
      if (!subscription?.active_plan_id) {
        setCurrentPlanPowerUps([]);
        return;
      }

      try {
        const { data, error } = await supabase
          .from('subscription_plans')
          .select('included_power_ups')
          .eq('plan_id', subscription.active_plan_id)
          .single();

        if (error) {
          console.error('Error fetching plan power-ups:', error);
          setCurrentPlanPowerUps([]);
          return;
        }

        setCurrentPlanPowerUps(data?.included_power_ups || []);
      } catch (err) {
        console.error('Error in fetchCurrentPlanPowerUps:', err);
        setCurrentPlanPowerUps([]);
      }
    };

    // Only fetch if subscription context is available
    if (subscription !== undefined) {
      fetchCurrentPlanPowerUps();
    }
  }, [subscription?.active_plan_id]);

  // Check if power-up is included in current subscription plan
  const isPowerUpIncludedInPlan = (powerUpSlug: string) => {
    // If no subscription context, allow access (fallback for development)
    if (!subscription || subscription === undefined) return true;
    if (!subscription.active_plan_id) return false;
    
    // Convert power-up slug to power-up ID format
    const powerUpId = powerUpSlug.includes('-powerup') ? powerUpSlug : `${powerUpSlug}-powerup`;
    
    return currentPlanPowerUps.includes(powerUpId);
  };

  const togglePowerUp = async (powerUpId: string) => {
    const isCurrentlyEnabled = enabledPowerUps.includes(powerUpId);
    const newState = !isCurrentlyEnabled;
    
    // Check subscription access before enabling (allow disabling always)
    if (newState && !isPowerUpIncludedInPlan(powerUpId)) {
      toast.error(`${powerUpId.replace('-', ' ')} power-up is not included in your current plan. Please upgrade to access this feature.`);
      return;
    }
    
    // Update UI optimistically
    setEnabledPowerUps(prev => 
      newState
        ? [...prev, powerUpId]
        : prev.filter(id => id !== powerUpId)
    );

    // Update backend
    const success = await togglePowerUpBackend(powerUpId, newState);
    if (!success) {
      // Revert on failure
      setEnabledPowerUps(prev => 
        !newState
          ? [...prev, powerUpId]
          : prev.filter(id => id !== powerUpId)
      );
      toast.error(`Failed to ${newState ? 'install' : 'uninstall'} power-up`);
    } else {
      toast.success(`Power-up ${newState ? 'installed' : 'uninstalled'} successfully`);
    }
  };


  // Get icon component matching the dropdown app menu style
  const getIconComponent = (iconName: string, size: string = "h-5 w-5", appSlug?: string) => {
    try {
      // Handle custom app icons (same as DraggableAppGrid)
      if (appSlug === 'proposals') {
        return (
          <img 
            src="/proposalsapp (2).png" 
            alt="Proposals" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'messages') {
        return (
          <img 
            src="/Message App.png" 
            alt="Messages" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'automations') {
        return (
          <img 
            src="/Automations.png" 
            alt="Automations" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'customers') {
        return (
          <img 
            src="/Customers.png" 
            alt="Customers" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'teams') {
        return (
          <img 
            src="/Teams.png" 
            alt="Teams" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'inventory') {
        return (
          <img 
            src="/inventory.png" 
            alt="Inventory" 
            className={`${size} object-contain`}
          />
        );
      }
      
      if (appSlug === 'crm') {
        return (
          <img 
            src="/CRM2.png" 
            alt="CRM" 
            className={`${size} object-contain`}
          />
        );
      }
      
      const IconComponent = (Icons[iconName as keyof typeof Icons] || Icons.HelpCircle) as React.ComponentType<{ className?: string }>;
      return <IconComponent className={size} />;
    } catch (error) {
      console.error('Error getting icon:', iconName, error);
      return <Icons.HelpCircle className={size} />;
    }
  };

  // Get display name for app (handles name mappings)
  const getAppDisplayName = () => {
    if (!app || !app.app) return '';
    
    // Handle specific name mappings
    const nameMap: { [key: string]: string } = {
      'mobile-calendar-core-app': 'Field-Pro',
      'm-calendar-core-app': 'Field-Pro',
      'automations-core-app': 'Automate',
      'products-addon-app': 'Store',
      'payroll-addon-app': 'T-Sheets',
    };
    
    // Check if we have a mapping for the app ID
    if (app.app.id && nameMap[app.app.id]) {
      return nameMap[app.app.id];
    }
    
    // Check if we have a mapping for the slug
    if (app.app.slug) {
      if (app.app.slug === 'mobile-calendar' || app.app.slug === 'm-calendar') {
        return 'Field-Pro';
      }
      if (app.app.slug === 'automations') {
        return 'Automate';
      }
      if (app.app.slug === 'products') {
        return 'Store';
      }
      if (app.app.slug === 'projects') {
        return 'Projects';
      }
      if (app.app.slug === 'payroll') {
        return 'T-Sheets';
      }
    }
    
    // Fall back to the name from the database
    return app.app.name || '';
  };

  const customAppIcons: { [key: string]: string } = {
    'analytics': '/Analyticsappicon.png',
    'projects': '/Projectsappicon.png',
    'calendar': '/Calenderappicon.png',
    'crm': '/CRMappicon.png',
    'proposals': '/Proposalsappicon.png',
    'tasks': '/Bookingsappicon.png',
    'inventory': '/Inventoryappicon.png',
    'expenses': '/Expensesappicon.png',
    'payroll': '/Timesheetsappicon.png',
    'teams': '/Teamsappicon.png',
    'invoices': '/Invoicesappicon.png',
    'automations': '/Automateappicon.png',
    'services': '/Servicesappicon.png',
    'customers': '/Customersappicon.png',
    'marketing': '/Marketingappicon.png',
    'gift-cards': '/Giftcardsappicon.png',
    'messages': '/Messagesappicon.png',
    'bookings': '/Bookingsappicon.png',
    'products': '/Storeappicon.png',
    'mobile-calendar': '/Fieldproappicon.png',
    'm-calendar': '/Fieldproappicon.png',
  };

  const getPlaceholderIcon = (appSlug: string, iconSize: string = "h-6 w-6") => {
    const iconMap: { [key: string]: React.ReactNode } = {
      'calendar': <Icons.Calendar className={iconSize} />,
      'mobile-calendar': <Icons.Smartphone className={iconSize} />,
      'm-calendar': <Icons.Smartphone className={iconSize} />,
      'customers': <Icons.Users className={iconSize} />,
      'automations': <Icons.Zap className={iconSize} />,
      'teams': <Icons.Users className={iconSize} />,
      'tasks': <Icons.ListChecks className={iconSize} />,
      'analytics': <Icons.BarChart3 className={iconSize} />,
      'crm': <Icons.Building2 className={iconSize} />,
      'inventory': <Icons.Package className={iconSize} />,
      'payroll': <Icons.DollarSign className={iconSize} />,
      'services': <Icons.Wrench className={iconSize} />,
      'bookings': <Icons.CalendarDays className={iconSize} />,
      'proposals': <Icons.FileText className={iconSize} />,
      'marketing': <Icons.Megaphone className={iconSize} />,
      'invoices': <Icons.Receipt className={iconSize} />,
      'messages': <Icons.MessageSquare className={iconSize} />,
      'products': <Icons.ShoppingCart className={iconSize} />,
      'projects': <Icons.Folder className={iconSize} />,
    };
    
    return iconMap[appSlug] || <Icons.Package className={iconSize} />;
  };

  const renderAppTile = (size: string = "w-10 h-10") => {
    if (!app) return null;
    
    const appSlug = app.app.slug;
    
    if (customAppIcons[appSlug]) {
      return (
        <div className={`${size} rounded-xl overflow-hidden bg-white shadow-md transition-all duration-200`}>
          <img src={customAppIcons[appSlug]} alt={appSlug} className="w-full h-full object-cover" draggable={false} style={{ imageRendering: '-webkit-optimize-contrast' as any }} />
        </div>
      );
    }
    
    return (
      <div className={`${size} bg-gradient-to-br from-blue-600 to-blue-500 rounded-xl flex items-center justify-center text-white shadow-md transition-all duration-200`}>
        {getPlaceholderIcon(appSlug, "h-5 w-5")}
      </div>
    );
  };

  if (!isOpen || !app) return null;

  return createPortal(
    <>
      {/* iOS-style backdrop with blur */}
      <div 
        className="fixed inset-0 transition-opacity duration-200"
        style={{
          zIndex: 100000,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(20px)',
          WebkitBackdropFilter: 'blur(20px)',
          opacity: 1,
          pointerEvents: 'auto'
        }}
        onClick={onClose}
      />
      
      {/* Modal - iOS Style */}
      <div className="fixed inset-0 flex items-center justify-center pointer-events-none z-[100001] sm:items-center">
        <div data-assistant-modal="app-settings" className="bg-white w-full h-full sm:rounded-[20px] sm:shadow-xl sm:max-w-2xl sm:mx-4 sm:max-h-[85vh] flex flex-col pointer-events-auto overflow-hidden" style={{ paddingTop: 'env(safe-area-inset-top, 0px)', paddingBottom: 'env(safe-area-inset-bottom, 0px)', paddingLeft: 'env(safe-area-inset-left, 0px)', paddingRight: 'env(safe-area-inset-right, 0px)' }}>
          {/* Header - iOS Style */}
          <div className="flex items-center justify-between px-4 sm:px-6 py-3 sm:py-4 border-b border-gray-200/60">
            <div className="flex items-center gap-2 sm:gap-3">
              {renderAppTile("w-8 h-8 sm:w-10 sm:h-10")}
              <h2 className="text-base sm:text-lg font-semibold text-gray-900">
                {getAppDisplayName()} Settings
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          
          {/* Tabs - iOS Style */}
          <div className="flex border-b border-gray-200/60 bg-gray-50/30">
            <button
              onClick={() => setActiveTab('settings')}
              className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                activeTab === 'settings' 
                  ? 'text-gray-900 border-b-2 border-blue-500 bg-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              App Settings
            </button>
            <button
              onClick={() => setActiveTab('powerups')}
              className={`flex-1 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium transition-colors touch-manipulation ${
                activeTab === 'powerups' 
                  ? 'text-gray-900 border-b-2 border-blue-500 bg-white' 
                  : 'text-gray-600 hover:text-gray-900'
              }`}
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              Power-Ups
            </button>
          </div>
          
          {/* Content */}
          <div className="flex-1 overflow-y-auto">
            {activeTab === 'settings' && app?.app.slug === 'calendar' ? (
              !calendarSettingsLoaded ? (
                <div className="p-4 sm:p-6 bg-gray-50 flex items-center justify-center min-h-[320px]">
                  <div className="h-8 w-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" aria-label="Loading settings" />
                </div>
              ) : (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                {/* Calendar Display */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Calendar Display</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden space-y-1">
                    {/* Default Calendar View Setting */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4">
                      <div className="flex items-center gap-3 flex-1 min-w-0 mb-3 sm:mb-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Default Calendar View</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Choose which view opens by default
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2 sm:ml-4">
                        <select
                          value={defaultCalendarView}
                          onChange={async (e) => {
                            const newValue = e.target.value as 'month' | 'week' | 'day';
                            setDefaultCalendarView(newValue);
                            await saveCalendarSetting('default_calendar_view', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'default_calendar_view', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className="block w-32 sm:w-36 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                        >
                          <option value="month">Month</option>
                          <option value="week">Week</option>
                          <option value="day">Day</option>
                        </select>
                      </div>
                    </label>

                    {/* Map View Toggle - available whether or not mobile booking is enabled */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Enable Map View</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show map in day view with collapsible bookings
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {mapViewEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !mapViewEnabled;
                            setMapViewEnabled(newValue);
                            await saveCalendarSetting('map_view_enabled', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'map_view_enabled', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            mapViewEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            mapViewEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Sales Pins Toggle - Only shown when Map View is enabled */}
                    {mapViewEnabled && (
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MapPin className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Sales Pins</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show sales address markers on day &amp; month map views
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {salesPinsEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !salesPinsEnabled;
                              setSalesPinsEnabled(newValue);
                              await saveCalendarSetting('sales_pins_enabled', newValue);
                              window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                                detail: { key: 'sales_pins_enabled', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              salesPinsEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              salesPinsEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    )}
                  </div>
                </div>

                {/* Quick Create — what shows in the + action menu */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Quick Create</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden space-y-1">
                    {/* Location Booking */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Location Booking</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Enable Bookings Connected To Your Fixed Location
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {showLocationBookingInFAB ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !showLocationBookingInFAB;
                            setShowLocationBookingInFAB(newValue);
                            await saveCalendarSetting('show_location_booking_in_fab', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'show_location_booking_in_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            showLocationBookingInFAB ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showLocationBookingInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Mobile Booking Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Smartphone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Mobile Booking</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Enable Bookings At Your Customer's Location
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {showMobileBookingInFAB ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !showMobileBookingInFAB;
                            setShowMobileBookingInFAB(newValue);
                            await saveCalendarSetting('show_mobile_booking_in_fab', newValue);
                            window.dispatchEvent(new CustomEvent('mobileCalendarSettingChanged', {
                              detail: { key: 'show_mobile_booking_in_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          role="switch"
                          aria-checked={showMobileBookingInFAB}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            showMobileBookingInFAB ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showMobileBookingInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Field Quotes (FAB + standalone scheduling) */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileSearch className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Field Quotes</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show in floating action menu and schedule without a project
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {showFieldQuotesInFAB ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !showFieldQuotesInFAB;
                            setShowFieldQuotesInFAB(newValue);
                            await saveCalendarSetting('show_field_quotes_in_fab', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'show_field_quotes_in_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            showFieldQuotesInFAB ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showFieldQuotesInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Nested: Create Field Quotes from bookings (only when Field Quotes enabled) */}
                    {showFieldQuotesInFAB && (
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer py-4 pr-4 pl-10 sm:pl-14 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100 bg-gray-50/50" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Create Field Quotes from bookings</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Add a button in the booking info modal to schedule a field quote that auto-links to the booking
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {createFieldQuotesFromBookings ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !createFieldQuotesFromBookings;
                              setCreateFieldQuotesFromBookings(newValue);
                              await saveCalendarSetting('create_field_quotes_from_bookings', newValue);
                              window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                                detail: { key: 'create_field_quotes_from_bookings', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              createFieldQuotesFromBookings ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              createFieldQuotesFromBookings ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    )}

                    {/* Events Toggle (calendar time blocks; key remains show_tasks_in_fab) */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Events</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show in floating action menu
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {showTasksInFAB ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !showTasksInFAB;
                            setShowTasksInFAB(newValue);
                            await saveCalendarSetting('show_tasks_in_fab', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'show_tasks_in_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            showTasksInFAB ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showTasksInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Mobile & Online Booking */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Mobile &amp; Online Booking</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden space-y-1">
                    {/* Service Areas Configure */}
                    <button
                      onClick={() => setShowServiceAreasModal(true)}
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Service Areas</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Set up geographic service areas
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                          Configure
                        </span>
                      </div>
                    </button>

                    {/* Bookings by Service Area Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Bookings by Service Area</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Require mobile bookings to match service areas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {bookingsByServiceAreaEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !bookingsByServiceAreaEnabled;
                            setBookingsByServiceAreaEnabled(newValue);
                            await saveCalendarSetting('mobile_booking_by_service_area', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'mobile_booking_by_service_area', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          role="switch"
                          aria-checked={bookingsByServiceAreaEnabled}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            bookingsByServiceAreaEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            bookingsByServiceAreaEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Buffer Time */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 border-t border-gray-100">
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Clock className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Buffer Time</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Time between mobile bookings to allow for travel
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <select
                          value={mobileBufferTime}
                          onChange={async (e) => {
                            const newValue = Number(e.target.value);
                            setMobileBufferTime(newValue);
                            await saveMobileBufferTime(newValue);
                          }}
                          disabled={!mobileBufferTimeLoaded || settingsLoading}
                          className="block w-36 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 disabled:text-gray-500"
                        >
                          {!mobileBufferTimeLoaded ? (
                            <option value="0">Loading...</option>
                          ) : (
                            <>
                              <option value="0">No buffer time</option>
                              <option value="30">30 minutes</option>
                              <option value="60">1 hour</option>
                              <option value="90">1.5 hours</option>
                              <option value="120">2 hours</option>
                            </>
                          )}
                        </select>
                      </div>
                    </div>

                    {/* Online Booking Configure */}
                    <button
                      onClick={() => {
                        navigate('/mobile-calendar/online-booking');
                        onClose();
                      }}
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left border-t border-gray-100"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Online Booking</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Set up online booking options
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                          Configure
                        </span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Field Operations */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Field Operations</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden space-y-1">
                    {/* Auto Job Tracking Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Clock className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Resource Working on Job</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Automatically start/stop job timers based on location proximity
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {autoJobTrackingEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !autoJobTrackingEnabled;
                            setAutoJobTrackingEnabled(newValue);
                            await saveCalendarSetting('auto_job_tracking_enabled', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'auto_job_tracking_enabled', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            autoJobTrackingEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            autoJobTrackingEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Range Input - Only shown when Auto Job Tracking is enabled */}
                    {autoJobTrackingEnabled && (
                      <div className="p-4 border-t border-gray-100">
                        <label className="block text-sm font-semibold text-gray-900 mb-2">
                          Proximity Range (km)
                        </label>
                        <div className="flex items-center gap-3">
                          <input
                            type="number"
                            min="0.1"
                            max="50"
                            step="0.1"
                            value={autoJobTrackingRangeKm}
                            onChange={(e) => {
                              const value = parseFloat(e.target.value);
                              if (!isNaN(value) && value >= 0.1 && value <= 50) {
                                setAutoJobTrackingRangeKm(value);
                              }
                            }}
                            onBlur={async () => {
                              await saveCalendarSetting('auto_job_tracking_range_km', autoJobTrackingRangeKm);
                              window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                                detail: { key: 'auto_job_tracking_range_km', value: autoJobTrackingRangeKm }
                              }));
                            }}
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            placeholder="2.0"
                          />
                          <span className="text-xs text-gray-500 whitespace-nowrap">km</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1.5">
                          Resources within this range of a job will automatically start tracking time
                        </p>
                      </div>
                    )}

                    {/* Current Bookings Helper Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Current Bookings Helper</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Flag bookings stuck in progress or past their start date so you can keep your schedule current
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {currentBookingsHelperEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !currentBookingsHelperEnabled;
                            setCurrentBookingsHelperEnabled(newValue);
                            await saveCalendarSetting('current_bookings_helper_enabled', newValue);
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'current_bookings_helper_enabled', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            currentBookingsHelperEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            currentBookingsHelperEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Feedback Settings */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Customer Experience</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Feedback Collection</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Enable customer feedback collection after bookings
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {feedbackEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !feedbackEnabled;
                            await saveFeedbackSettings(newValue);
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            feedbackEnabled ? 'bg-blue-600' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            feedbackEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Custom Feedback URL — sub-toggle under Feedback Collection */}
                    {feedbackEnabled && (
                      <div className="border-t border-gray-100">
                        <label className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex-1 min-w-0 pl-11">
                            <div className="text-[13px] font-medium text-gray-800">Custom Feedback URL</div>
                            <p className="text-xs text-gray-500 mt-0.5">Use your own subdomain for feedback links</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowBrandedFeedbackUrl((v) => !v);
                            }}
                            className={`relative inline-flex h-6 w-11 sm:h-5 sm:w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ml-4 ${
                              showBrandedFeedbackUrl ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`pointer-events-none inline-block h-5 w-5 sm:h-4 sm:w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showBrandedFeedbackUrl ? 'translate-x-5 sm:translate-x-4' : 'translate-x-0'
                            }`} />
                          </button>
                        </label>
                        {showBrandedFeedbackUrl && selectedBusiness?.id && (
                          <BrandedFeedbackHostSection
                            businessId={selectedBusiness.id}
                            onUpdated={() => forceLoadBusinesses()}
                          />
                        )}
                      </div>
                    )}
                    
                    {/* Customize Form Button - Only show when enabled */}
                    {feedbackEnabled && (
                      <div className="px-4 pb-4">
                        <button
                          onClick={() => setIsCustomizeFeedbackModalOpen(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Customize Form
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Liability Waiver Settings */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Legal Protection</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Liability Waiver</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Require customers to sign waivers before service completion
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {liabilityWaiverEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !liabilityWaiverEnabled;
                            await saveLiabilityWaiverSettings(newValue);
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            liabilityWaiverEnabled ? 'bg-blue-600' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            liabilityWaiverEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>
                    
                    {/* Customize Waiver Button - Only show when enabled */}
                    {liabilityWaiverEnabled && (
                      <div className="px-4 pb-4">
                        <button
                          onClick={() => setIsCustomizeLiabilityWaiverModalOpen(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Customize Waiver
                        </button>
                      </div>
                    )}
                  </div>
                </div>

              </div>
              )
            ) : activeTab === 'settings' && app?.app.slug === 'mobile-calendar' ? (
              <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                {/* Mobile Bookings Settings */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Mobile Bookings</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden space-y-1">
                    {/* Mobile Booking Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Smartphone className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Mobile Booking</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show in floating action menu
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {showMobileBookingInFAB ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !showMobileBookingInFAB;
                            setShowMobileBookingInFAB(newValue);
                            await saveCalendarSetting('show_mobile_booking_in_fab', newValue);
                            
                            window.dispatchEvent(new CustomEvent('mobileCalendarSettingChanged', {
                              detail: { key: 'show_mobile_booking_in_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          role="switch"
                          aria-checked={showMobileBookingInFAB}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            showMobileBookingInFAB ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            showMobileBookingInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Service Areas Configure */}
                    <button
                      onClick={() => setShowServiceAreasModal(true)}
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left border-t border-gray-100"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Service Areas</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Set up geographic service areas
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                          Configure
                        </span>
                      </div>
                    </button>

                    {/* Bookings by Service Area Toggle */}
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation border-t border-gray-100" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MapPin className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Bookings by Service Area</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Require mobile bookings to match service areas
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {bookingsByServiceAreaEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !bookingsByServiceAreaEnabled;
                            setBookingsByServiceAreaEnabled(newValue);
                            await saveCalendarSetting('mobile_booking_by_service_area', newValue);
                            
                            window.dispatchEvent(new CustomEvent('calendarSettingChanged', {
                              detail: { key: 'mobile_booking_by_service_area', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          role="switch"
                          aria-checked={bookingsByServiceAreaEnabled}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            bookingsByServiceAreaEnabled ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            bookingsByServiceAreaEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Online Booking Configure */}
                    <button
                      onClick={() => {
                        navigate('/mobile-calendar/online-booking');
                        onClose();
                      }}
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left border-t border-gray-100"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Calendar className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Online Booking</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Set up online booking options
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                          Configure
                        </span>
                      </div>
                    </button>
                  </div>
                </div>

                {/* Feedback Settings */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Customer Experience</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <MessageSquare className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Feedback Collection</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Enable customer feedback collection after bookings
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {feedbackEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !feedbackEnabled;
                            await saveFeedbackSettings(newValue);
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            feedbackEnabled ? 'bg-blue-600' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            feedbackEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>

                    {/* Custom Feedback URL — sub-toggle under Feedback Collection */}
                    {feedbackEnabled && (
                      <div className="border-t border-gray-100">
                        <label className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex-1 min-w-0 pl-11">
                            <div className="text-[13px] font-medium text-gray-800">Custom Feedback URL</div>
                            <p className="text-xs text-gray-500 mt-0.5">Use your own subdomain for feedback links</p>
                          </div>
                          <button
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setShowBrandedFeedbackUrl((v) => !v);
                            }}
                            className={`relative inline-flex h-6 w-11 sm:h-5 sm:w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ml-4 ${
                              showBrandedFeedbackUrl ? 'bg-blue-500' : 'bg-gray-300'
                            }`}
                          >
                            <span className={`pointer-events-none inline-block h-5 w-5 sm:h-4 sm:w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showBrandedFeedbackUrl ? 'translate-x-5 sm:translate-x-4' : 'translate-x-0'
                            }`} />
                          </button>
                        </label>
                        {showBrandedFeedbackUrl && selectedBusiness?.id && (
                          <BrandedFeedbackHostSection
                            businessId={selectedBusiness.id}
                            onUpdated={() => forceLoadBusinesses()}
                          />
                        )}
                      </div>
                    )}
                    
                    {/* Customize Form Button - Only show when enabled */}
                    {feedbackEnabled && (
                      <div className="px-4 pb-4">
                        <button
                          onClick={() => setIsCustomizeFeedbackModalOpen(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Customize Form
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Liability Waiver Settings */}
                <div>
                  <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Legal Protection</h3>
                  <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                    <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Liability Waiver</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Require customers to sign waivers before service completion
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                        <span className="text-xs text-gray-500 sm:hidden">
                          {liabilityWaiverEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <button
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !liabilityWaiverEnabled;
                            await saveLiabilityWaiverSettings(newValue);
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            liabilityWaiverEnabled ? 'bg-blue-600' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            liabilityWaiverEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`} />
                        </button>
                      </div>
                    </label>
                    
                    {/* Customize Waiver Button - Only show when enabled */}
                    {liabilityWaiverEnabled && (
                      <div className="px-4 pb-4">
                        <button
                          onClick={() => setIsCustomizeLiabilityWaiverModalOpen(true)}
                          className="w-full sm:w-auto inline-flex items-center justify-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          Customize Waiver
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {/* Future Settings Placeholder */}
                <div className="text-center py-8 border-t border-gray-200/60">
                  <p className="text-sm text-gray-500">
                    Additional mobile calendar settings will be available in future updates.
                  </p>
                </div>
              </div>
            ) : activeTab === 'powerups' && app?.app.slug === 'calendar' ? (
              <div className="p-6">
                <div className="text-center py-12">
                  <Calendar className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                  <p className="text-sm text-gray-500">
                    This app doesn't have any power-ups at this time.
                  </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && app?.app.slug === 'mobile-calendar' ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Smartphone className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isServicesApp ? (
                <div className="p-6">
                  <p className="text-sm text-gray-500 mb-6">
                    Enhance your Services with powerful add-on features. Enable the ones you need.
                  </p>
                  
                  <div className="space-y-3">
                    {/* Workflows Power-Up */}
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-start justify-between">
                          <div className="flex items-start gap-3">
                            <div className="w-9 h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5">
                              <GitBranch className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="text-sm font-medium text-gray-900">Workflows</h4>
                                {enabledPowerUps.includes('workflows') && (
                                  <span className="px-2 py-0.5 text-xs bg-green-500/20 text-green-400 rounded-full">
                                    Installed
                                  </span>
                                )}
                                {/* Plan inclusion indicator */}
                                {isPowerUpIncludedInPlan('workflows') ? (
                                  <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-blue-400 rounded-full">
                                    Included
                                  </span>
                                ) : (
                                  <span className="px-2 py-0.5 text-xs bg-blue-500/20 text-orange-400 rounded-full">
                                    Upgrade Required
                                  </span>
                                )}
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Create automated workflows and step-by-step processes for your services. Perfect for standardizing operations.
                              </p>
                            </div>
                          </div>
                          <button
                            onClick={() => togglePowerUp('workflows')}
                            disabled={!enabledPowerUps.includes('workflows') && !isPowerUpIncludedInPlan('workflows')}
                            className={`px-3 py-1.5 text-xs font-medium rounded-xl transition-all ${
                              enabledPowerUps.includes('workflows')
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : isPowerUpIncludedInPlan('workflows')
                                ? 'bg-blue-600 hover:bg-blue-700 text-white'
                                : 'bg-gray-300 text-gray-500 cursor-not-allowed opacity-50'
                            }`}
                          >
                            {enabledPowerUps.includes('workflows') ? 'Uninstall' : 
                             isPowerUpIncludedInPlan('workflows') ? 'Install' : 'Upgrade Required'}
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'settings' && isTeamsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Features</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-orange-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Assets</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Enable assets to assign equipment, vehicles, and other items to bookings
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {assetsEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !assetsEnabled;
                              setAssetsEnabled(newValue);
                              await saveCalendarSetting('assets_enabled', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              assetsEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              assetsEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Additional team settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isTeamsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isAutomationsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Automate Settings</h3>
                    <p className="text-sm text-gray-500">
                      Automation app settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isAutomationsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Zap className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isProposalsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Create Proposal From Action Menu Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Action Menu</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Proposal</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show in floating action menu
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showProposalInFAB ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showProposalInFAB;
                              setShowProposalInFAB(newValue);
                              await saveCalendarSetting('show_proposal_in_fab', newValue);
                              
                              window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                detail: { key: 'show_proposal_in_fab', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showProposalInFAB ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showProposalInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Branded proposal domain — same DNS / Vercel flow as CRM Live Intake Forms */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">
                      Customer links
                    </h3>
                    <div
                      className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    >
                      <div className="px-4 pt-4 pb-2">
                        <h3 className="text-sm font-semibold text-gray-900">Public proposal links</h3>
                        <p className="text-xs text-gray-500 mt-0.5">
                          Set an optional branded domain so proposal URLs use your subdomain (same setup steps as Live
                          Intake Forms in CRM settings).
                        </p>
                      </div>
                      {selectedBusiness?.id && (
                        <BrandedProposalHostSection
                          businessId={selectedBusiness.id}
                          onUpdated={() => forceLoadBusinesses({ silent: true })}
                        />
                      )}
                    </div>
                  </div>

                  {/* Google Earth Measurements Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Features</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Globe className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Google Earth Measurements</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Enable satellite-based area and distance measurements on proposals
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {googleEarthMeasurementsEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !googleEarthMeasurementsEnabled;
                              setGoogleEarthMeasurementsEnabled(newValue);
                              await saveCalendarSetting('google_earth_measurements_enabled', newValue);
                              
                              window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                detail: { key: 'google_earth_measurements_enabled', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              googleEarthMeasurementsEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              googleEarthMeasurementsEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Email Settings */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Email</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Attach PDF in email</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Include the estimate PDF as an attachment when emailing a proposal
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {attachPdfInEmail ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !attachPdfInEmail;
                              setAttachPdfInEmail(newValue);
                              await saveCalendarSetting('attach_pdf_in_email', newValue);
                              window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                detail: { key: 'attach_pdf_in_email', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              attachPdfInEmail ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              attachPdfInEmail ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Public Proposal View */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Public Proposal View</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden p-4">
                      <p className="text-xs text-gray-500 mb-3">How offers appear when a customer opens the public proposal.</p>
                      <div className="grid grid-cols-3 gap-1.5 bg-gray-100 rounded-xl p-1">
                        {([
                          ['standard', 'Standard', 'Collapsed'],
                          ['partial', 'Partial', 'Snippet + expand'],
                          ['open', 'Open', 'All expanded'],
                        ] as const).map(([value, label, hint]) => (
                          <button
                            key={value}
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setPublicProposalView(value);
                              await saveCalendarSetting('public_proposal_view', value);
                              window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                detail: { key: 'public_proposal_view', value }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`flex flex-col items-center justify-center rounded-lg px-2 py-2 text-center transition-all ${
                              publicProposalView === value
                                ? 'bg-white shadow-sm'
                                : 'hover:bg-white/50'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`text-[13px] font-semibold ${publicProposalView === value ? 'text-blue-600' : 'text-gray-700'}`}>{label}</span>
                            <span className="text-[10px] text-gray-400 mt-0.5 leading-tight">{hint}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Logo display */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Logo</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-indigo-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Image className="h-4 w-4 text-indigo-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Display logo only</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Hide the business name and show only the logo on the public proposal
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {logoOnly ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !logoOnly;
                              setLogoOnly(newValue);
                              await saveCalendarSetting('logo_only', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              logoOnly ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              logoOnly ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                        <p className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Logo size</p>
                        <div className="grid grid-cols-3 gap-1.5 bg-gray-100 rounded-xl p-1">
                          {([
                            ['small', 'Small'],
                            ['medium', 'Medium'],
                            ['large', 'Large'],
                          ] as const).map(([value, label]) => (
                            <button
                              key={value}
                              type="button"
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setLogoSize(value);
                                await saveCalendarSetting('logo_size', value);
                              }}
                              disabled={settingsLoading}
                              className={`flex items-center justify-center rounded-lg px-2 py-2 text-center transition-all ${
                                logoSize === value
                                  ? 'bg-white shadow-sm'
                                  : 'hover:bg-white/50'
                              } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className={`text-[13px] font-semibold ${logoSize === value ? 'text-blue-600' : 'text-gray-700'}`}>
                                {label}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Custom SMS / Email send messages */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Send Messages</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                      {/* Custom SMS */}
                      <div>
                        <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Smartphone className="h-4 w-4 text-emerald-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Custom SMS</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Prefill a custom message when sending a proposal by SMS
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                            <span className="text-xs text-gray-500 sm:hidden">
                              {customSmsMessageEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newValue = !customSmsMessageEnabled;
                                setCustomSmsMessageEnabled(newValue);
                                await saveCalendarSetting('custom_sms_message_enabled', newValue);
                                if (newValue && !customSmsMessage.trim()) {
                                  const { DEFAULT_PROPOSAL_SMS_TEMPLATE } = await import('../lib/proposalSendDefaults');
                                  setCustomSmsMessage(DEFAULT_PROPOSAL_SMS_TEMPLATE);
                                }
                              }}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                customSmsMessageEnabled ? 'bg-blue-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                customSmsMessageEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        </label>
                        {customSmsMessageEnabled && (
                          <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              SMS message
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                              Placeholders: {'{first_name}'}, {'{url}'}, {'{proposal_title}'}, {'{business_name}'}
                            </p>
                            {customSmsMessage !== savedCustomSmsMessage && (
                              <p className="text-xs text-amber-600 mb-2 font-medium">
                                Unsaved changes
                              </p>
                            )}
                            <textarea
                              value={customSmsMessage}
                              onChange={(e) => setCustomSmsMessage(e.target.value)}
                              placeholder="Hey {first_name}, please use the link below to view the proposal details&#10;&#10;{url}"
                              rows={4}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[96px] ${
                                customSmsMessage !== savedCustomSmsMessage
                                  ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                  : 'border-gray-300'
                              }`}
                            />
                            {customSmsMessage !== savedCustomSmsMessage && (
                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      setCustomSmsMessageSaving(true);
                                      await saveCalendarSetting('custom_sms_message', customSmsMessage);
                                      setSavedCustomSmsMessage(customSmsMessage);
                                    } finally {
                                      setCustomSmsMessageSaving(false);
                                    }
                                  }}
                                  disabled={customSmsMessageSaving || settingsLoading}
                                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {customSmsMessageSaving ? 'Saving…' : 'Save message'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Custom Email */}
                      <div>
                        <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-sky-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Mail className="h-4 w-4 text-sky-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Custom Email</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Prefill a custom message when sending a proposal by email
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                            <span className="text-xs text-gray-500 sm:hidden">
                              {customEmailMessageEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newValue = !customEmailMessageEnabled;
                                setCustomEmailMessageEnabled(newValue);
                                await saveCalendarSetting('custom_email_message_enabled', newValue);
                                if (newValue && !customEmailMessage.trim()) {
                                  const { DEFAULT_PROPOSAL_EMAIL_BODY_TEMPLATE } = await import('../lib/proposalSendDefaults');
                                  setCustomEmailMessage(DEFAULT_PROPOSAL_EMAIL_BODY_TEMPLATE);
                                }
                              }}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                customEmailMessageEnabled ? 'bg-blue-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                customEmailMessageEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        </label>
                        {customEmailMessageEnabled && (
                          <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              Email message
                            </label>
                            <p className="text-xs text-gray-400 mb-2">
                              Placeholders: {'{first_name}'}, {'{proposal_title}'}, {'{business_name}'}
                            </p>
                            {customEmailMessage !== savedCustomEmailMessage && (
                              <p className="text-xs text-amber-600 mb-2 font-medium">
                                Unsaved changes
                              </p>
                            )}
                            <textarea
                              value={customEmailMessage}
                              onChange={(e) => setCustomEmailMessage(e.target.value)}
                              placeholder="Hi {first_name},&#10;&#10;Thank you for asking us to quote on your project."
                              rows={5}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[120px] ${
                                customEmailMessage !== savedCustomEmailMessage
                                  ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                  : 'border-gray-300'
                              }`}
                            />
                            {customEmailMessage !== savedCustomEmailMessage && (
                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={async () => {
                                    try {
                                      setCustomEmailMessageSaving(true);
                                      await saveCalendarSetting('custom_email_message', customEmailMessage);
                                      setSavedCustomEmailMessage(customEmailMessage);
                                    } finally {
                                      setCustomEmailMessageSaving(false);
                                    }
                                  }}
                                  disabled={customEmailMessageSaving || settingsLoading}
                                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {customEmailMessageSaving ? 'Saving…' : 'Save message'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Thank You Message */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Thank You Page</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-green-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-4 w-4 text-green-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Custom thank you message</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show a custom message below the accepted offer on the public thank you page
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {thankYouMessageEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !thankYouMessageEnabled;
                              setThankYouMessageEnabled(newValue);
                              await saveCalendarSetting('thank_you_message_enabled', newValue);
                              window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                detail: { key: 'thank_you_message_enabled', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              thankYouMessageEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              thankYouMessageEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      {thankYouMessageEnabled && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            Message
                          </label>
                          {thankYouMessage !== savedThankYouMessage && (
                            <p className="text-xs text-amber-600 mb-2 font-medium">
                              Unsaved changes
                            </p>
                          )}
                          <textarea
                            value={thankYouMessage}
                            onChange={(e) => setThankYouMessage(e.target.value)}
                            placeholder="e.g. Thanks for choosing us! We'll call within 24 hours to schedule."
                            rows={4}
                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[96px] ${
                              thankYouMessage !== savedThankYouMessage
                                ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                : 'border-gray-300'
                            }`}
                          />
                          {thankYouMessage !== savedThankYouMessage && (
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    setThankYouMessageSaving(true);
                                    await saveCalendarSetting('thank_you_message', thankYouMessage);
                                    setSavedThankYouMessage(thankYouMessage);
                                    window.dispatchEvent(new CustomEvent('proposalsSettingChanged', {
                                      detail: { key: 'thank_you_message', value: thankYouMessage }
                                    }));
                                  } finally {
                                    setThankYouMessageSaving(false);
                                  }
                                }}
                                disabled={thankYouMessageSaving || settingsLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {thankYouMessageSaving ? 'Saving…' : 'Save message'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Google Reviews Badge */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Public Proposal</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Star className="h-4 w-4 text-amber-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Google Reviews Badge</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show your star rating below the business name on the public proposal
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {googleReviewsBadgeEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !googleReviewsBadgeEnabled;
                              setGoogleReviewsBadgeEnabled(newValue);
                              await saveCalendarSetting('google_reviews_badge_enabled', newValue);
                              // Persist the default rating when enabling so the public badge can render.
                              if (newValue) {
                                await saveCalendarSetting('google_reviews_rating', googleReviewsRating || 5);
                              }
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              googleReviewsBadgeEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              googleReviewsBadgeEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      {googleReviewsBadgeEnabled && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3 space-y-3 sm:space-y-4">
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              Star Rating (out of 5)
                            </label>
                            <input
                              type="number"
                              min="0"
                              max="5"
                              step="0.1"
                              value={googleReviewsRating}
                              onChange={(e) => {
                                const v = Math.min(5, Math.max(0, parseFloat(e.target.value) || 0));
                                setGoogleReviewsRating(v);
                              }}
                              onBlur={async (e) => {
                                const v = Math.min(5, Math.max(0, parseFloat(e.target.value) || 0));
                                setGoogleReviewsRating(v);
                                await saveCalendarSetting('google_reviews_rating', v);
                              }}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              placeholder="4.9"
                            />
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              Review Count <span className="font-normal text-gray-400 normal-case tracking-normal">(optional)</span>
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="1"
                              value={googleReviewsCount ?? ''}
                              onChange={(e) => {
                                const raw = e.target.value.trim();
                                setGoogleReviewsCount(raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0));
                              }}
                              onBlur={async (e) => {
                                const raw = e.target.value.trim();
                                const next = raw === '' ? null : Math.max(0, parseInt(raw, 10) || 0);
                                setGoogleReviewsCount(next);
                                await saveCalendarSetting('google_reviews_count', next);
                              }}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              placeholder="100"
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                              Shown as "based on {googleReviewsCount || 100}+ reviews" — leave empty to hide
                            </p>
                          </div>
                          <div>
                            <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                              Google Reviews Link <span className="font-normal text-gray-400 normal-case tracking-normal">(optional)</span>
                            </label>
                            <input
                              type="url"
                              value={googleReviewsUrl}
                              onChange={(e) => setGoogleReviewsUrl(e.target.value)}
                              onBlur={async (e) => {
                                const trimmed = e.target.value.trim();
                                setGoogleReviewsUrl(trimmed);
                                await saveCalendarSetting('google_reviews_url', trimmed);
                              }}
                              className="block w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                              placeholder="https://g.page/r/..."
                            />
                            <p className="text-xs text-gray-500 mt-1.5">
                              When set, tapping the badge opens your Google reviews
                            </p>
                          </div>
                          <div className="p-3 bg-gray-50 border border-gray-200 rounded-lg">
                            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-400 mb-1.5">Preview</p>
                            <div className="flex items-center justify-center gap-1.5">
                              <svg width={15} height={15} viewBox="0 0 48 48" aria-hidden="true">
                                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
                                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
                                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
                                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
                              </svg>
                              <span className="text-amber-400 text-[14px] leading-none tracking-tight">
                                {'★'.repeat(Math.round(Math.min(5, Math.max(0, googleReviewsRating))))}
                                <span className="text-gray-300">{'★'.repeat(5 - Math.round(Math.min(5, Math.max(0, googleReviewsRating))))}</span>
                              </span>
                              <span className="text-[12px] font-semibold text-gray-900">{googleReviewsRating}</span>
                              {googleReviewsCount != null && googleReviewsCount > 0 && (
                                <span className="text-[12px] text-gray-500">· based on {googleReviewsCount}+ reviews</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Custom public message */}
                  <div>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Custom public message</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show a custom message below the contact line on the public proposal
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {publicMessageEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !publicMessageEnabled;
                              setPublicMessageEnabled(newValue);
                              await saveCalendarSetting('public_message_enabled', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              publicMessageEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              publicMessageEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      {publicMessageEnabled && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            Message
                          </label>
                          {publicMessage !== savedPublicMessage && (
                            <p className="text-xs text-amber-600 mb-2 font-medium">
                              Unsaved changes
                            </p>
                          )}
                          <textarea
                            value={publicMessage}
                            onChange={(e) => setPublicMessage(e.target.value)}
                            placeholder="e.g. Financing available. Ask us about payment plans."
                            rows={4}
                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[96px] ${
                              publicMessage !== savedPublicMessage
                                ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                : 'border-gray-300'
                            }`}
                          />
                          {publicMessage !== savedPublicMessage && (
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    setPublicMessageSaving(true);
                                    await saveCalendarSetting('public_message', publicMessage);
                                    setSavedPublicMessage(publicMessage);
                                  } finally {
                                    setPublicMessageSaving(false);
                                  }
                                }}
                                disabled={publicMessageSaving || settingsLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {publicMessageSaving ? 'Saving…' : 'Save message'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : activeTab === 'settings' && isProjectsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Create Project From Action Menu Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Action Menu</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <ClipboardList className="h-4 w-4 text-violet-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Project</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show in floating action menu. When created from the menu, the project opens automatically.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showProjectInFAB ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showProjectInFAB;
                              setShowProjectInFAB(newValue);
                              await saveCalendarSetting('show_project_in_fab', newValue);
                              window.dispatchEvent(new CustomEvent('projectsSettingChanged', {
                                detail: { key: 'show_project_in_fab', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showProjectInFAB ? 'bg-violet-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showProjectInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Additional project settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isExpensesApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Create Expenses From Action Menu Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Action Menu</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Receipt className="h-4 w-4 text-emerald-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Create Expenses from FAB Menu</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show "Upload Expense" button in floating action menu
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {createExpensesFromFAB ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !createExpensesFromFAB;
                              setCreateExpensesFromFAB(newValue);
                              await saveExpensesSetting('create_expenses_from_fab', newValue);
                              
                              window.dispatchEvent(new CustomEvent('expensesSettingChanged', {
                                detail: { key: 'create_expenses_from_fab', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              createExpensesFromFAB ? 'bg-emerald-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              createExpensesFromFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Auto Match POs Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Automation</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <ShoppingCart className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Auto Match POs</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              When a scanned receipt references a purchase order, attach the invoice to the PO's expense automatically — or create the bill and receive the PO if no expense exists yet
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {autoMatchPOs ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !autoMatchPOs;
                              setAutoMatchPOs(newValue);
                              await saveExpensesSetting('auto_match_pos', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              autoMatchPOs ? 'bg-emerald-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              autoMatchPOs ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Future Settings Placeholder */}
                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Additional expense settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isDocsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Action Menu</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <FileText className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Complete Documents from FAB Menu</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Adds a “Complete Document” option to the floating action menu so your team can fill out and submit forms quickly.
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {documentsFabEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              await saveDocumentsFabEnabled(!documentsFabEnabled);
                            }}
                            disabled={docsSettingSaving}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              documentsFabEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${docsSettingSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              documentsFabEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Manage your document templates and completed forms from the Documents page.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isCustomersApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Sales</h3>
                    <div className="bg-white border border-gray-200 rounded-xl p-4">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Icons.UserCheck className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">Active customers</div>
                          <p className="text-xs text-gray-500 mt-1">
                            Active customers is a segment on the Customers page. Open Customers, tap Segment, and turn on Active customers to choose the invoice window and show that list.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isProposalsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <FileText className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isProjectsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <ClipboardList className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isInventoryApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Withdraw Stock From Quick Action Menu Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Quick Actions</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Withdraw Stock</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show in floating action menu
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showWithdrawStockInFAB ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showWithdrawStockInFAB;
                              setShowWithdrawStockInFAB(newValue);
                              await saveCalendarSetting('show_withdraw_stock_in_fab', newValue);
                              
                              window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                detail: { key: 'show_withdraw_stock_in_fab', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showWithdrawStockInFAB ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showWithdrawStockInFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Add Inventory to Bookings Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Booking Integration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Package className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Add Inventory to Bookings</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Allow adding inventory items to bookings
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showInventoryInBookings ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showInventoryInBookings;
                              setShowInventoryInBookings(newValue);
                              await saveCalendarSetting('show_inventory_in_bookings', newValue);
                              
                              window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                detail: { key: 'show_inventory_in_bookings', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showInventoryInBookings ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showInventoryInBookings ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Track Materials on Bookings Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Materials Management</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4 space-y-4">
                        <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Package className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Track Materials on Bookings</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Show Materials tab in booking info modal
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                            <span className="text-xs text-gray-500 sm:hidden">
                              {showMaterialsInBookings ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newValue = !showMaterialsInBookings;
                                setShowMaterialsInBookings(newValue);
                                await saveCalendarSetting('show_materials_in_bookings', newValue);
                                
                                window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                  detail: { key: 'show_materials_in_bookings', value: newValue }
                                }));
                              }}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                showMaterialsInBookings ? 'bg-blue-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                showMaterialsInBookings ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        </label>
                        
                        {/* Configure Materials Button - Only show when enabled */}
                        {showMaterialsInBookings && (
                          <div className="flex justify-end pt-2">
                            <button
                              onClick={() => navigate('/inventory/materials')}
                              className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              Configure Materials
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Orders settings */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Orders</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden divide-y divide-gray-100">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 cursor-pointer active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Pin className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Pin Orders Widget</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Pin the orders widget to your inventory dashboard
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {pinOrdersWidget ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !pinOrdersWidget;
                              setPinOrdersWidget(newValue);
                              await saveCalendarSetting('pin_orders_widget', newValue);
                              
                              window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                detail: { key: 'pin_orders_widget', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              pinOrdersWidget ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              pinOrdersWidget ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>

                      {/* What the pinned widget shows (only relevant when pinned) */}
                      {pinOrdersWidget && (
                        <div className="px-4 py-3 bg-gray-50/60 space-y-2.5">
                          {([
                            {
                              key: 'pin_orders_show_pos' as const,
                              label: 'Purchase orders',
                              description: 'Supplier POs awaiting delivery',
                              value: pinShowPos,
                              setValue: setPinShowPos,
                            },
                            {
                              key: 'pin_orders_show_orders' as const,
                              label: 'Customer orders',
                              description: 'Open orders from your online store and staff',
                              value: pinShowOrders,
                              setValue: setPinShowOrders,
                            },
                          ]).map(sub => (
                            <div key={sub.key} className="flex items-center justify-between gap-3 pl-11">
                              <div className="min-w-0">
                                <div className="text-[13px] font-medium text-gray-800">{sub.label}</div>
                                <p className="text-xs text-gray-500">{sub.description}</p>
                              </div>
                              <button
                                onClick={async (e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  const newValue = !sub.value;
                                  sub.setValue(newValue);
                                  await saveCalendarSetting(sub.key, newValue);
                                  window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                    detail: { key: sub.key, value: newValue }
                                  }));
                                }}
                                disabled={settingsLoading}
                                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                  sub.value ? 'bg-blue-500' : 'bg-gray-300'
                                } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <span className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                  sub.value ? 'translate-x-5' : 'translate-x-0'
                                }`} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 cursor-pointer active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-violet-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Sparkles className="h-4 w-4 text-violet-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">AI PO Data</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              When a supplier replies to a quote, automatically fill blank line prices and the ready-for-pickup date (uses AI tokens)
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {aiPoDataEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !aiPoDataEnabled;
                              setAiPoDataEnabled(newValue);
                              await saveCalendarSetting('ai_po_data', newValue);
                              window.dispatchEvent(new CustomEvent('inventorySettingChanged', {
                                detail: { key: 'ai_po_data', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              aiPoDataEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              aiPoDataEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isInventoryApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Package className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isInvoicesApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isProductsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Configure Online Store Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Store Configuration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => navigate('/online-store')}
                        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <ShoppingCart className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Online Store</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Set up store settings and appearance
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 sm:mt-0 sm:ml-4">
                          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                            Configure
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {/* Future Settings Placeholder */}
                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Additional product settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isProductsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <ShoppingCart className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isPayrollApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Clock In from FAB Menu Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Clock In</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Clock className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Clock in from FAB Menu</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Allow resources to clock in from the blue plus FAB menu icon
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showClockInFromFAB ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showClockInFromFAB;
                              setShowClockInFromFAB(newValue);
                              await saveCalendarSetting('clock_in_from_fab', newValue);
                              
                              window.dispatchEvent(new CustomEvent('payrollSettingChanged', {
                                detail: { key: 'clock_in_from_fab', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showClockInFromFAB ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showClockInFromFAB ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>

                      {/* Clock in URL Setting */}
                      <div className="border-t border-gray-100">
                        <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Clock className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Clock in URL</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Allow resources to clock in using a shared URL and passcode
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                            <span className="text-xs text-gray-500 sm:hidden">
                              {clockInUrlEnabled ? 'Enabled' : 'Disabled'}
                            </span>
                            <button
                              onClick={async (e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                const newValue = !clockInUrlEnabled;
                                setClockInUrlEnabled(newValue);
                                await saveCalendarSetting('clock_in_url_enabled', newValue);
                                
                                window.dispatchEvent(new CustomEvent('payrollSettingChanged', {
                                  detail: { key: 'clock_in_url_enabled', value: newValue }
                                }));
                              }}
                              disabled={settingsLoading}
                              className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                                clockInUrlEnabled ? 'bg-blue-500' : 'bg-gray-300'
                              } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                clockInUrlEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                              }`} />
                            </button>
                          </div>
                        </label>

                        {clockInUrlEnabled && (
                          <div className="px-4 pb-4 pt-0 border-t border-gray-100">
                            <div className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                              <div className="flex items-center justify-between gap-2 mb-2">
                                <span className="text-xs font-medium text-gray-700">Clock in URL</span>
                                <button
                                  type="button"
                                  onClick={async () => {
                                    const url = getFullUrl('/payroll/pincode');
                                    try {
                                      await navigator.clipboard.writeText(url);
                                      toast.success('Clock in URL copied to clipboard!');
                                    } catch {
                                      toast.error('Failed to copy URL');
                                    }
                                  }}
                                  className="flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 hover:text-blue-700 hover:bg-blue-50 rounded-lg transition-colors touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <Copy className="h-3.5 w-3.5" />
                                  Copy
                                </button>
                              </div>
                              <code className="text-blue-600 text-xs break-all font-medium">
                                {getFullUrl('/payroll/pincode')}
                              </code>
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              Set each resource&apos;s 4-digit passcode in their profile
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Enable Flat Rate Time Tracking On Bookings Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Booking Integration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <DollarSign className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Flat Rate Time Tracking</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show RateBook tab in booking info modal
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {showFlatRateInBookings ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !showFlatRateInBookings;
                              setShowFlatRateInBookings(newValue);
                              await saveCalendarSetting('show_flat_rate_in_bookings', newValue);
                              
                              window.dispatchEvent(new CustomEvent('payrollSettingChanged', {
                                detail: { key: 'show_flat_rate_in_bookings', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              showFlatRateInBookings ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              showFlatRateInBookings ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Configure Flat Rate Time Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Time Configuration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <button
                        onClick={() => {
                          navigate('/flat-rates');
                          onClose();
                        }}
                        className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Clock className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Flat Rate Time</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Set up flat rate time configurations
                            </p>
                          </div>
                        </div>
                        <div className="mt-3 sm:mt-0 sm:ml-4">
                          <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                            Configure
                          </span>
                        </div>
                      </button>
                    </div>
                  </div>

                  {selectedBusiness?.id && (
                    <PayrollPeriodSettingsForm businessId={selectedBusiness.id} />
                  )}
                </div>
              ) : activeTab === 'powerups' && isPayrollApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <DollarSign className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isCRMApp ? (
                <div className="p-4 sm:p-6 space-y-5">
                  {/* Lead Forms Configure */}
                  <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <button
                      type="button"
                      onClick={() => {
                        navigate('/crm/lead-forms');
                        onClose();
                      }}
                      className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                          <FileText className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Lead Forms</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Create shareable forms that capture leads into CRM
                          </p>
                        </div>
                      </div>
                      <div className="mt-3 sm:mt-0 sm:ml-4">
                        <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                          Configure
                        </span>
                      </div>
                    </button>
                  </div>

                  {/* Lost Reason Settings */}
                  <div className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden" style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
                    <div className="px-4 pt-4 pb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Lost Reason Tracking</h3>
                      <p className="text-xs text-gray-500 mt-0.5">Record why leads are marked as lost</p>
                    </div>

                    {/* Toggle */}
                    <label className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-red-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <AlertTriangle className="h-4 w-4 text-red-500" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Record Lost Reason</div>
                          <p className="text-xs text-gray-500 mt-0.5">Prompt for reason when marking a lead as lost</p>
                        </div>
                      </div>
                      <button
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmRecordLostReason;
                          setCrmRecordLostReason(newValue);
                          await saveCRMSetting('record_lost_reason', newValue);
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmRecordLostReason ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                          crmRecordLostReason ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                        }`} />
                      </button>
                    </label>

                    {/* Reason Management (only shown when toggle is on) */}
                    {crmRecordLostReason && (
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                        <div className="text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">Lost Reasons</div>

                        {/* Add new reason */}
                        <div className="flex items-center gap-2 mb-3">
                          <input
                            type="text"
                            value={newLostReason}
                            onChange={(e) => setNewLostReason(e.target.value)}
                            onKeyDown={(e) => { if (e.key === 'Enter') addLostReason(); }}
                            placeholder="Add a reason..."
                            className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                          />
                          <button
                            onClick={addLostReason}
                            disabled={!newLostReason.trim() || crmLostReasonLoading}
                            className={`p-2 rounded-lg transition-colors touch-manipulation ${
                              newLostReason.trim() && !crmLostReasonLoading
                                ? 'bg-blue-500 text-white hover:bg-blue-600 active:bg-blue-700'
                                : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                            }`}
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </div>

                        {/* Existing reasons list */}
                        {crmLostReasons.length === 0 ? (
                          <p className="text-xs text-gray-400 text-center py-3">No reasons added yet</p>
                        ) : (
                          <div className="space-y-1">
                            {crmLostReasons.map((reason) => (
                              <div key={reason.id} className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg group">
                                <span className="text-sm text-gray-700">{reason.reason}</span>
                                <button
                                  onClick={() => deleteLostReason(reason.id)}
                                  disabled={crmLostReasonLoading}
                                  className="p-1 text-gray-400 hover:text-red-500 active:text-red-700 transition-colors touch-manipulation sm:opacity-0 sm:group-hover:opacity-100"
                                >
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Custom Fields */}
                  <div
                    className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <div className="px-4 pt-4 pb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Custom Fields</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Add your own fields to lead creation — text, number, or dropdown
                      </p>
                    </div>
                    <label
                      className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-purple-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <SlidersHorizontal className="h-4 w-4 text-purple-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Enable Custom Fields</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show configured custom fields when creating leads in Nexus. Required fields only apply here — webhooks and automations are never blocked.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmCustomFieldsEnabled;
                          setCrmCustomFieldsEnabled(newValue);
                          if (!newValue) setShowCrmCustomFieldsModal(false);
                          await saveCRMSetting('custom_fields_enabled', newValue);
                          window.dispatchEvent(new CustomEvent('crmSettingChanged', {
                            detail: { key: 'custom_fields_enabled', value: newValue }
                          }));
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmCustomFieldsEnabled ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            crmCustomFieldsEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>
                    {crmCustomFieldsEnabled && (
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                        <button
                          type="button"
                          onClick={() => setShowCrmCustomFieldsModal(true)}
                          className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-200 hover:bg-gray-100 active:scale-[0.99] transition touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <SlidersHorizontal className="h-4 w-4 text-gray-600" />
                          Configure custom fields
                        </button>
                      </div>
                    )}
                  </div>

                  {/* No Activity Alerts */}
                  <div
                    className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <div className="px-4 pt-4 pb-2">
                      <h3 className="text-sm font-semibold text-gray-900">No Activity Alerts</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Surface open leads in Control Centre when they go quiet with no follow-up scheduled
                      </p>
                    </div>
                    <label
                      className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-orange-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <ZapOff className="h-4 w-4 text-orange-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">No Activity</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Show stale leads in Actions Required after a set period
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmNoActivityEnabled;
                          setCrmNoActivityEnabled(newValue);
                          await saveCRMSetting(NO_ACTIVITY_ALERT_ENABLED_KEY, newValue);
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmNoActivityEnabled ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            crmNoActivityEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>

                    {crmNoActivityEnabled ? (
                      <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                          Inactivity threshold
                        </label>
                        <div className="flex items-center gap-2">
                          <input
                            type="number"
                            min={MIN_NO_ACTIVITY_HOURS}
                            max={MAX_NO_ACTIVITY_HOURS}
                            value={crmNoActivityHours}
                            onChange={(e) => {
                              const next = parseNoActivityHours(e.target.value);
                              setCrmNoActivityHours(next);
                            }}
                            onBlur={async () => {
                              await saveCRMSetting(NO_ACTIVITY_HOURS_KEY, crmNoActivityHours);
                            }}
                            className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-400"
                          />
                          <span className="text-sm text-gray-600">hours</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-2">
                          Open leads with no CRM activity and no future follow-up scheduled will appear in
                          Control Centre Actions Required after this period.
                        </p>
                        <div className="flex flex-wrap gap-2 mt-3">
                          {[24, 48, 72].map((hours) => (
                            <button
                              key={hours}
                              type="button"
                              onClick={async () => {
                                setCrmNoActivityHours(hours);
                                await saveCRMSetting(NO_ACTIVITY_HOURS_KEY, hours);
                              }}
                              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors touch-manipulation ${
                                crmNoActivityHours === hours
                                  ? 'bg-blue-100 text-blue-700'
                                  : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                              }`}
                            >
                              {hours}h
                            </button>
                          ))}
                        </div>
                      </div>
                    ) : null}
                  </div>

                  {/* Live Intake Forms */}
                  <div
                    className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <div className="px-4 pt-4 pb-2">
                      <h3 className="text-sm font-semibold text-gray-900">Live Intake Forms</h3>
                      <p className="text-xs text-gray-500 mt-0.5">
                        Turn on to set an optional branded intake domain, build templates, and send live intake links from
                        the CRM.
                      </p>
                    </div>
                    <label
                      className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-emerald-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <ClipboardList className="h-4 w-4 text-emerald-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Enable Live Intake Forms</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Turn on to design templates and prepare for live customer intake.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmLiveIntakeForms;
                          setCrmLiveIntakeForms(newValue);
                          if (!newValue) setShowLiveIntakeConfigureModal(false);
                          await saveCRMSetting('live_intake_forms', newValue);
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmLiveIntakeForms ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            crmLiveIntakeForms ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>
                    {crmLiveIntakeForms && selectedBusiness?.id && (
                      <>
                        <BrandedIntakeHostSection
                          businessId={selectedBusiness.id}
                          onUpdated={() => forceLoadBusinesses({ silent: true })}
                        />
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <button
                            type="button"
                            onClick={() => setShowLiveIntakeConfigureModal(true)}
                            className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl text-sm font-semibold text-gray-800 bg-gray-50 border border-gray-200 hover:bg-gray-100 active:scale-[0.99] transition touch-manipulation"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <SlidersHorizontal className="h-4 w-4 text-gray-600" />
                            Configure templates
                          </button>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Create Opportunity from FAB */}
                  <div
                    className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <label
                      className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <Target className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">Create Opportunity from FAB Menu</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            Add a "New Opportunity" option to the floating create (+) menu.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmCreateOpportunityFromFab;
                          setCrmCreateOpportunityFromFab(newValue);
                          await saveCRMSetting('create_opportunity_from_fab', newValue);
                          window.dispatchEvent(new CustomEvent('crmSettingChanged', {
                            detail: { key: 'create_opportunity_from_fab', value: newValue }
                          }));
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmCreateOpportunityFromFab ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            crmCreateOpportunityFromFab ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>
                  </div>

                  {/* AI Create from Business Card */}
                  <div
                    className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                    style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                  >
                    <label
                      className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                    >
                      <div className="flex items-center gap-3 flex-1 min-w-0">
                        <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                          <ScanLine className="h-4 w-4 text-blue-600" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-semibold text-gray-900">AI Create from Business Card</div>
                          <p className="text-xs text-gray-500 mt-0.5">
                            When creating a lead, offer "Create from Photo" — AI reads a business card, matches or creates the customer, and opens the lead.
                          </p>
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={async (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const newValue = !crmAiCreateFromCard;
                          setCrmAiCreateFromCard(newValue);
                          await saveCRMSetting('ai_create_from_business_card', newValue);
                          window.dispatchEvent(new CustomEvent('crmSettingChanged', {
                            detail: { key: 'ai_create_from_business_card', value: newValue }
                          }));
                        }}
                        disabled={settingsLoading}
                        className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                          crmAiCreateFromCard ? 'bg-blue-500' : 'bg-gray-300'
                        } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                      >
                        <span
                          className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                            crmAiCreateFromCard ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                          }`}
                        />
                      </button>
                    </label>

                    {/* Nested: Default Source — only when card scanning is on */}
                    {crmAiCreateFromCard && (
                      <div className="border-t border-gray-100 bg-gray-50/60">
                        <label
                          className="flex items-center justify-between cursor-pointer pl-[60px] pr-4 py-3 active:bg-gray-100/60 transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Default Source</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Apply a fixed lead source to every business card scan
                            </p>
                          </div>
                          <button
                            type="button"
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !crmCardDefaultSourceEnabled;
                              setCrmCardDefaultSourceEnabled(newValue);
                              await saveCRMSetting('business_card_default_source_enabled', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              crmCardDefaultSourceEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                                crmCardDefaultSourceEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                              }`}
                            />
                          </button>
                        </label>

                        {crmCardDefaultSourceEnabled && (
                          <div className="pl-[60px] pr-4 pb-3.5">
                            <input
                              type="text"
                              value={crmCardDefaultSource}
                              onChange={(e) => setCrmCardDefaultSource(e.target.value)}
                              onBlur={async () => {
                                await saveCRMSetting('business_card_default_source', crmCardDefaultSource.trim());
                              }}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                              }}
                              placeholder='e.g. "Trade Show", "Networking Event"'
                              className="w-full px-3 py-2.5 bg-white border border-gray-200 rounded-xl text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-blue-500/40 focus:border-blue-400 transition-colors"
                            />
                            <p className="mt-1 text-[11px] text-gray-400">
                              Saved when you tap away. Leave blank to keep "Business Card".
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ) : activeTab === 'powerups' && isCRMApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <Users className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isMarketingApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Google Ads Integration Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Google Ads Integration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-4">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Google Ads</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Connect your Google Ads account to analyze campaign performance
                            </p>
                          </div>
                        </div>
                        
                        {/* Google Ads Connect Button */}
                        <div>
                          <GoogleAdsConnectButton 
                            size="medium" 
                            className="w-full"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Sales Map */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Sales Map</h3>
                    <div
                      className="bg-white rounded-2xl border border-gray-200/80 overflow-hidden"
                      style={{ boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}
                    >
                      <label
                        className="flex items-center justify-between cursor-pointer px-4 py-3 active:bg-gray-50 transition-colors touch-manipulation"
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                      >
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MapPin className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Create Sales Pin</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Add a "Sales Pin" option to the floating create (+) menu — drop a pin at your location while door-knocking and optionally capture a lead.
                            </p>
                          </div>
                        </div>
                        <button
                          type="button"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const newValue = !marketingCreateSalesPin;
                            setMarketingCreateSalesPin(newValue);
                            await saveMarketingSetting('create_sales_pin_from_fab', newValue);
                            window.dispatchEvent(new CustomEvent('marketingSettingChanged', {
                              detail: { key: 'create_sales_pin_from_fab', value: newValue }
                            }));
                          }}
                          disabled={settingsLoading}
                          className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                            marketingCreateSalesPin ? 'bg-blue-500' : 'bg-gray-300'
                          } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          <span
                            className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              marketingCreateSalesPin ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`}
                          />
                        </button>
                      </label>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isMarketingApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isAnalyticsApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Web Analytics Setting */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Web Analytics</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <BarChart3 className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Web Analytics</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Track website visits and user behavior
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {webAnalyticsEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !webAnalyticsEnabled;
                              setWebAnalyticsEnabled(newValue);
                              
                              if (newValue) {
                                try {
                                  setSettingsLoading(true);
                                  const { analyticsService } = await import('../lib/analytics');
                                  const trackingCode = analyticsService.generateTrackingCode(selectedBusiness?.id || '');
                                  await analyticsService.saveConfig({
                                    business_id: selectedBusiness?.id || '',
                                    provider: 'custom',
                                    is_active: true,
                                    tracking_code: trackingCode
                                  });
                                  toast.success('Web Analytics enabled successfully');
                                } catch (error) {
                                  console.error('Error enabling analytics:', error);
                                  setWebAnalyticsEnabled(false);
                                  toast.error('Failed to enable Web Analytics');
                                } finally {
                                  setSettingsLoading(false);
                                }
                              } else {
                                try {
                                  setSettingsLoading(true);
                                  const { analyticsService } = await import('../lib/analytics');
                                  await analyticsService.saveConfig({
                                    business_id: selectedBusiness?.id || '',
                                    provider: 'custom',
                                    is_active: false,
                                    tracking_code: undefined
                                  });
                                  toast.success('Web Analytics disabled successfully');
                                } catch (error) {
                                  console.error('Error disabling analytics:', error);
                                  setWebAnalyticsEnabled(true);
                                  toast.error('Failed to disable Web Analytics');
                                } finally {
                                  setSettingsLoading(false);
                                }
                              }
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              webAnalyticsEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              webAnalyticsEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      
                      {/* Tracking Code Button - Only show when enabled */}
                      {webAnalyticsEnabled && (
                        <>
                          <div className="border-t border-gray-200"></div>
                          <button
                            onClick={() => setShowAnalyticsModal(true)}
                            className="w-full flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 active:bg-gray-50 transition-colors touch-manipulation text-left"
                            style={{ WebkitTapHighlightColor: 'transparent' }}
                          >
                            <div className="flex items-center gap-3 flex-1 min-w-0">
                              <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                                <FileText className="h-4 w-4 text-blue-600" />
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-semibold text-gray-900">Get Tracking Code</div>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  Copy the tracking code for your website
                                </p>
                              </div>
                            </div>
                            <div className="mt-3 sm:mt-0 sm:ml-4">
                              <span className="inline-flex items-center px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 rounded-lg">
                                Get Code
                              </span>
                            </div>
                          </button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* Future Settings Placeholder */}
                  <div className="text-center py-8 border-t border-gray-200/60">
                    <p className="text-sm text-gray-500">
                      Additional analytics settings will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isMessagesApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Twilio Integration – status only; manage from Integrations page */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Twilio Integration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <MessageSquare className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">SMS & Voice</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Enable SMS messaging and voice calls
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center sm:flex-shrink-0">
                            <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg ${
                              twilioEnabled ? 'text-green-700 bg-green-50' : 'text-gray-600 bg-gray-100'
                            }`}>
                              {twilioEnabled ? 'Connected' : 'Not connected'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 px-1">
                          Manage connection Nexus Connect → Integrations
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Call Recording & Transcription */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Call Recording</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex items-center justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-red-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Smartphone className="h-4 w-4 text-red-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Record & transcribe calls</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Save a recording and text transcript with every call
                              </p>
                            </div>
                          </div>
                          <button
                            type="button"
                            onClick={handleCallRecordingToggle}
                            disabled={!twilioEnabled || callRecordingSaving}
                            aria-label="Toggle call recording"
                            className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                              callRecordingEnabled ? 'bg-blue-600' : 'bg-gray-300'
                            } ${!twilioEnabled || callRecordingSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                callRecordingEnabled ? 'translate-x-6' : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        {!twilioEnabled ? (
                          <p className="text-xs text-gray-500 mt-2 px-1">
                            Connect Twilio first to enable call recording.
                          </p>
                        ) : callRecordingEnabled ? (
                          <div className="mt-3 pt-3 border-t border-gray-100 space-y-2">
                            <p className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider px-1">
                              Recording mode
                            </p>
                            {([
                              {
                                value: 'transcribe_only' as const,
                                label: 'Transcription only',
                                desc: 'Audio deleted after transcribing — transcript only, no storage fees',
                              },
                              {
                                value: 'transcribe_store' as const,
                                label: 'Transcribe & store',
                                desc: 'Keep recordings for playback — Twilio storage fees apply',
                              },
                            ]).map((option) => {
                              const isActive = callRecordingMode === option.value;
                              return (
                                <button
                                  key={option.value}
                                  type="button"
                                  disabled={callRecordingSaving}
                                  onClick={() => {
                                    // Switching modes always requires re-accepting the
                                    // compliance terms; clicking the active mode does nothing.
                                    if (isActive) return;
                                    setPendingRecordingMode(option.value);
                                    setShowRecordingComplianceModal(true);
                                  }}
                                  className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                    isActive
                                      ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500/30'
                                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                  } ${callRecordingSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                                >
                                  <div className={`w-4 h-4 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                    isActive ? 'border-blue-600' : 'border-gray-300'
                                  }`}>
                                    {isActive && <div className="w-2 h-2 rounded-full bg-blue-600" />}
                                  </div>
                                  <div className="min-w-0">
                                    <div className={`text-sm font-medium ${isActive ? 'text-blue-900' : 'text-gray-900'}`}>
                                      {option.label}
                                    </div>
                                    <div className="text-xs text-gray-500 mt-0.5">{option.desc}</div>
                                  </div>
                                </button>
                              );
                            })}
                            {/* Caller notice toggle */}
                            <div className="flex items-center justify-between gap-3 p-3 rounded-xl border border-gray-200">
                              <div className="min-w-0">
                                <div className="text-sm font-medium text-gray-900">Caller notice</div>
                                <div className="text-xs text-gray-500 mt-0.5">
                                  {callRecordingNoticeEnabled
                                    ? 'Inbound callers hear "this call may be recorded" before connecting'
                                    : 'No announcement — you are responsible for obtaining consent'}
                                </div>
                              </div>
                              <button
                                type="button"
                                onClick={handleRecordingNoticeToggle}
                                disabled={callRecordingSaving}
                                aria-label="Toggle caller recording notice"
                                className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors ${
                                  callRecordingNoticeEnabled ? 'bg-blue-600' : 'bg-gray-300'
                                } ${callRecordingSaving ? 'opacity-50 cursor-not-allowed' : ''}`}
                              >
                                <span
                                  className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                                    callRecordingNoticeEnabled ? 'translate-x-6' : 'translate-x-1'
                                  }`}
                                />
                              </button>
                            </div>
                            <p className="text-[11px] text-gray-400 px-1">
                              Transcription uses 5,000 AI tokens per minute.
                            </p>
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </div>

                  {/* Gmail Integration – status only; manage from Integrations page */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Gmail Integration</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <Mail className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Email Integration</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Connect Gmail for email automation
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center sm:flex-shrink-0">
                            <span className={`inline-flex items-center px-3 py-1.5 text-xs font-medium rounded-lg ${
                              gmailEnabled ? 'text-green-700 bg-green-50' : 'text-gray-600 bg-gray-100'
                            }`}>
                              {gmailEnabled ? 'Connected' : 'Not connected'}
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-500 mt-2 px-1">
                          Manage connection Nexus Connect → Integrations
                        </p>
                        {/* Email signature – only when Gmail integration is active */}
                        {gmailEnabled && (
                          <div className="mt-4 pt-4 border-t border-gray-200">
                            <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider mb-2">
                              Email signature
                            </label>
                            <p className="text-xs text-gray-500 mb-2">
                              This signature will be appended to emails you send from the Messages app.
                            </p>
                            {emailSignature !== savedEmailSignature && (
                              <p className="text-xs text-amber-600 mb-2 font-medium">
                                Unsaved changes
                              </p>
                            )}
                            <textarea
                              value={emailSignature}
                              onChange={(e) => setEmailSignature(e.target.value)}
                              placeholder={'e.g. Best regards,\nYour Name\nYour Title | Business Name\nPhone | Email'}
                              rows={5}
                              className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y min-h-[100px] ${
                                emailSignature !== savedEmailSignature
                                  ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                  : 'border-gray-300'
                              }`}
                            />
                            {emailSignature !== savedEmailSignature && (
                              <div className="mt-2 flex justify-end">
                                <button
                                  type="button"
                                  onClick={saveMessagesEmailSignature}
                                  disabled={emailSignatureSaving}
                                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                  {emailSignatureSaving ? 'Saving…' : 'Save signature'}
                                </button>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Message Organization */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Message Organization</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <div className="p-4">
                        <p className="text-xs text-gray-500 mb-3">
                          Choose how unread messages are organized in the pipeline.
                        </p>
                        <div className="space-y-2">
                          {([
                            { value: 'chronological' as const, label: 'Chronological', desc: 'All messages sorted by most recent' },
                            { value: 'sms_first' as const, label: 'SMS First', desc: 'SMS messages at the top, then emails' },
                            { value: 'email_first' as const, label: 'Email First', desc: 'Emails at the top, then SMS messages' },
                          ]).map((option) => (
                            <button
                              key={option.value}
                              type="button"
                              disabled={messageOrganizationSaving}
                              onClick={() => saveMessageOrganization(option.value)}
                              className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-all text-left ${
                                messageOrganization === option.value
                                  ? 'border-blue-500 bg-blue-50/60 ring-1 ring-blue-500/30'
                                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                              } ${messageOrganizationSaving ? 'opacity-60 cursor-not-allowed' : ''}`}
                            >
                              <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                                messageOrganization === option.value
                                  ? 'border-blue-500 bg-blue-500'
                                  : 'border-gray-300'
                              }`}>
                                {messageOrganization === option.value && (
                                  <Check className="h-3 w-3 text-white" />
                                )}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-gray-900">{option.label}</div>
                                <p className="text-xs text-gray-500 mt-0.5">{option.desc}</p>
                              </div>
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'settings' && isInvoicesApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Stripe Integration Setting - Hidden for now, will show again later */}
                  {false && (
                    <div>
                      <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Payment Processing</h3>
                      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                        <div className="p-4">
                          <div className="flex items-center gap-3 mb-4">
                            <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                              <CreditCard className="h-4 w-4 text-blue-600" />
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="text-sm font-semibold text-gray-900">Stripe Integration</div>
                              <p className="text-xs text-gray-500 mt-0.5">
                                Connect Stripe to accept payments for invoices
                              </p>
                            </div>
                          </div>
                        
                        {/* Stripe Connect Button */}
                        <div className="mt-4">
                          <StripeConnectButton 
                            size="medium" 
                            className="w-full"
                          />
                        </div>
                        
                        {stripeConnected && stripeAccountId && (
                          <div className="mt-4 space-y-3">
                            <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-xl">
                              <div className="flex items-center gap-2">
                                <Check className="h-4 w-4 text-green-400" />
                                <span className="text-sm text-green-400 font-medium">Stripe Connected</span>
                              </div>
                              <p className="text-xs text-gray-500 mt-1">
                                Account ID: {stripeAccountId?.slice(0, 12)}...
                              </p>
                            </div>
                            
                            {/* Disconnect Button */}
                            <button
                              onClick={async () => {
                                if (!confirm('Are you sure you want to disconnect your Stripe account? This will disable payment processing for invoices.')) {
                                  return;
                                }
                                
                                try {
                                  setSettingsLoading(true);
                                  const { error } = await supabase
                                    .from('businesses')
                                    .update({
                                      stripe_connect_account_id: null,
                                      stripe_connect_onboarding_completed: false,
                                      stripe_connect_charges_enabled: false,
                                    })
                                    .eq('id', selectedBusiness?.id);

                                  if (error) {
                                    console.error('Failed to disconnect Stripe:', error);
                                    toast.error('Failed to disconnect Stripe. Please try again.');
                                    return;
                                  }

                                  // Refresh the business data
                                  window.location.reload(); // Simple refresh to update the state
                                  toast.success('Stripe account disconnected successfully!');
                                } catch (error) {
                                  console.error('Error disconnecting Stripe:', error);
                                  toast.error('Failed to disconnect Stripe. Please try again.');
                                } finally {
                                  setSettingsLoading(false);
                                }
                              }}
                              disabled={settingsLoading}
                              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600/10 hover:bg-red-600/20 border border-red-500/30 text-red-400 rounded-xl transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {settingsLoading ? (
                                <>
                                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-red-400"></div>
                                  <span className="text-sm font-medium">Disconnecting...</span>
                                </>
                              ) : (
                                <>
                                  <Unlink className="h-4 w-4" />
                                  <span className="text-sm font-medium">Disconnect Stripe</span>
                                </>
                              )}
                            </button>
                          </div>
                        )}
                        </div>
                      </div>
                    </div>
                  )}

                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Subscriptions</h3>
                    <div className={`bg-white border border-gray-200 rounded-xl overflow-hidden ${!stripeConnected ? 'opacity-50' : ''}`}>
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icons.RefreshCw className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Enable Subscriptions</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {stripeConnected
                                ? 'Create recurring subscriptions for your customers'
                                : 'Connect Stripe to enable this feature'}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {enableSubscriptions ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              if (!stripeConnected) return;
                              const newValue = !enableSubscriptions;
                              setEnableSubscriptions(newValue);
                              await saveServicesSetting('enable_subscriptions', newValue);

                              window.dispatchEvent(new CustomEvent('servicesSettingChanged', {
                                detail: { key: 'enable_subscriptions', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading || !stripeConnected}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              enableSubscriptions ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading || !stripeConnected ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              enableSubscriptions ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Custom message */}
                  <div>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <MessageSquare className="h-4 w-4 text-blue-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Custom message</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Show a custom message on the public invoice payment screen
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {publicMessageEnabled ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !publicMessageEnabled;
                              setPublicMessageEnabled(newValue);
                              await saveCalendarSetting('public_message_enabled', newValue);
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              publicMessageEnabled ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              publicMessageEnabled ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                      {publicMessageEnabled && (
                        <div className="border-t border-gray-100 px-4 pb-4 pt-3">
                          <label className="block text-xs font-medium text-gray-500 uppercase tracking-wider mb-2">
                            Message
                          </label>
                          {publicMessage !== savedPublicMessage && (
                            <p className="text-xs text-amber-600 mb-2 font-medium">
                              Unsaved changes
                            </p>
                          )}
                          <textarea
                            value={publicMessage}
                            onChange={(e) => setPublicMessage(e.target.value)}
                            placeholder="e.g. Prefer e-Transfer? Send payment to payments@yourbusiness.com with your invoice number in the memo."
                            rows={4}
                            className={`w-full px-3 py-2 text-sm border rounded-lg focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 resize-y min-h-[96px] ${
                              publicMessage !== savedPublicMessage
                                ? 'border-amber-400 ring-1 ring-amber-400/50 bg-amber-50/30'
                                : 'border-gray-300'
                            }`}
                          />
                          {publicMessage !== savedPublicMessage && (
                            <div className="mt-2 flex justify-end">
                              <button
                                type="button"
                                onClick={async () => {
                                  try {
                                    setPublicMessageSaving(true);
                                    await saveCalendarSetting('public_message', publicMessage);
                                    setSavedPublicMessage(publicMessage);
                                  } finally {
                                    setPublicMessageSaving(false);
                                  }
                                }}
                                disabled={publicMessageSaving || settingsLoading}
                                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                              >
                                {publicMessageSaving ? 'Saving…' : 'Save message'}
                              </button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isInvoicesApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <CreditCard className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Invoices Power-Ups</h3>
                    <p className="text-sm text-gray-500">
                      Invoices power-ups will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'settings' && isServicesApp ? (
                <div className="p-4 sm:p-6 space-y-4 sm:space-y-5 bg-gray-50">
                  {/* Compare-at Pricing Toggle */}
                  <div>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2 sm:mb-3 px-1">Pricing</h3>
                    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
                      <label className="flex flex-col sm:flex-row sm:items-center sm:justify-between cursor-pointer p-4 active:bg-gray-50 transition-colors touch-manipulation" style={{ WebkitTapHighlightColor: 'transparent' }}>
                        <div className="flex items-center gap-3 flex-1 min-w-0">
                          <div className="w-8 h-8 sm:w-9 sm:h-9 bg-purple-100 rounded-xl flex items-center justify-center flex-shrink-0">
                            <Icons.Tag className="h-4 w-4 text-purple-600" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-gray-900">Compare-at Pricing</div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              Add an optional "compare at" price to service price options
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center justify-between sm:justify-end gap-3 mt-3 sm:mt-0 sm:ml-4">
                          <span className="text-xs text-gray-500 sm:hidden">
                            {compareAtPricing ? 'Enabled' : 'Disabled'}
                          </span>
                          <button
                            onClick={async (e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              const newValue = !compareAtPricing;
                              setCompareAtPricing(newValue);
                              await saveServicesSetting('compare_at_pricing', newValue);

                              window.dispatchEvent(new CustomEvent('servicesSettingChanged', {
                                detail: { key: 'compare_at_pricing', value: newValue }
                              }));
                            }}
                            disabled={settingsLoading}
                            className={`relative inline-flex h-7 w-12 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none ${
                              compareAtPricing ? 'bg-blue-500' : 'bg-gray-300'
                            } ${settingsLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            <span className={`pointer-events-none inline-block h-6 w-6 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                              compareAtPricing ? 'translate-x-5 sm:translate-x-5' : 'translate-x-0'
                            }`} />
                          </button>
                        </div>
                      </label>
                    </div>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isMessagesApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <MessageSquare className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Power-Ups Available</h3>
                    <p className="text-sm text-gray-500">
                      This app doesn't have any power-ups at this time.
                    </p>
                  </div>
                </div>
              ) : activeTab === 'powerups' && isAnalyticsApp ? (
                <div className="p-6">
                  <div className="text-center py-12">
                    <BarChart3 className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Analytics Power-Ups</h3>
                    <p className="text-sm text-gray-500">
                      Analytics power-ups will be available in future updates.
                    </p>
                  </div>
                </div>
              ) : (
                <div className="p-6">
                  <p className="text-gray-500 text-sm">
                    App settings will be configured here.
                  </p>
                </div>
              )}
          </div>
        </div>
      </div>

      {/* Analytics Settings Modal */}
      {isAnalyticsApp && (
        <AnalyticsSettings 
          isOpen={showAnalyticsModal} 
          onClose={() => {
            setShowAnalyticsModal(false);
            // Refresh analytics settings when modal closes
            if (app?.app.slug === 'analytics') {
              loadAnalyticsSettings();
            }
          }} 
        />
      )}

      {/* Twilio Settings Modal */}
      {isMessagesApp && (
        <TwilioSettings 
          isOpen={showTwilioModal} 
          onClose={() => setShowTwilioModal(false)}
          onSave={() => {
            // Reload Twilio settings after save
            loadTwilioSettings();
          }}
        />
      )}

      {/* Gmail Settings Modal */}
      {isMessagesApp && (
        <GmailSettings 
          isOpen={showGmailModal} 
          onClose={() => setShowGmailModal(false)}
          onSave={() => {
            // Reload Gmail settings after save
            loadGmailSettings();
          }}
        />
      )}

      {/* Customize Feedback Form Modal - Shared between Calendar apps */}
      {isCalendarApp && (
        <CustomizeFeedbackFormModal 
          isOpen={isCustomizeFeedbackModalOpen} 
          onClose={() => setIsCustomizeFeedbackModalOpen(false)}
        />
      )}

      {/* Customize Liability Waiver Modal - Shared between Calendar apps */}
      {isCalendarApp && (
        <CustomizeLiabilityWaiverModal 
          isOpen={isCustomizeLiabilityWaiverModalOpen} 
          onClose={() => setIsCustomizeLiabilityWaiverModalOpen(false)}
        />
      )}

      {/* Service Areas Management Modal - For calendar and mobile-calendar apps */}
      {(app?.app.slug === 'mobile-calendar' || app?.app.slug === 'calendar') && (
        <ServiceAreasManagementModal
          isOpen={showServiceAreasModal}
          onClose={() => setShowServiceAreasModal(false)}
        />
      )}

      {isCRMApp && selectedBusiness?.id && (
        <CrmCustomFieldsModal
          isOpen={showCrmCustomFieldsModal}
          onClose={() => setShowCrmCustomFieldsModal(false)}
          businessId={selectedBusiness.id}
        />
      )}

      {isCRMApp && selectedBusiness?.id && (
        <LiveIntakeFormsConfigureModal
          isOpen={showLiveIntakeConfigureModal}
          onClose={() => setShowLiveIntakeConfigureModal(false)}
          businessId={selectedBusiness.id}
        />
      )}

      {/* Caller-notice disable warning — turning off the automatic announcement */}
      {isMessagesApp && showNoticeWarningModal && (
        <div
          className="fixed inset-0 z-[100310] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="notice-warning-title"
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowNoticeWarningModal(false)}
            aria-hidden
          />
          <div className="relative w-full max-w-md flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 pt-5 pb-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-amber-100">
                <AlertTriangle className="h-5 w-5 text-amber-600" />
              </div>
              <div className="min-w-0">
                <h2 id="notice-warning-title" className="text-base font-semibold text-gray-900">
                  Turn off the recording notice?
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">Compliance warning — please read</p>
              </div>
            </div>

            <div className="space-y-3 px-5 py-4 text-[13px] leading-relaxed text-gray-700">
              <p>
                Calls will continue to be <span className="font-medium">recorded and transcribed</span>,
                but inbound callers will <span className="font-medium">no longer hear</span> the automatic
                announcement:
              </p>
              <p className="rounded-lg bg-gray-50 px-3 py-2 italic text-gray-600">
                "This call may be recorded for quality and record-keeping purposes."
              </p>
              <p>
                Many jurisdictions require notifying — or getting consent from — <span className="font-medium">all
                parties</span> before recording a call. By turning this notice off, you confirm that
                <span className="font-medium"> your business takes full responsibility</span> for informing
                callers and complying with the recording and privacy laws that apply to you and your callers.
              </p>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowNoticeWarningModal(false)}
                disabled={callRecordingSaving}
                className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              >
                Keep notice on
              </button>
              <button
                type="button"
                onClick={() => void saveRecordingNoticeEnabled(false)}
                disabled={callRecordingSaving}
                className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-amber-700 disabled:opacity-60"
              >
                {callRecordingSaving ? 'Saving…' : 'I understand — turn off notice'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Call recording compliance acceptance — gate for enabling the toggle */}
      {isMessagesApp && showRecordingComplianceModal && (
        <div
          className="fixed inset-0 z-[100300] flex items-center justify-center p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="call-recording-compliance-title"
        >
          <div
            className="absolute inset-0 bg-black/40 backdrop-blur-sm"
            onClick={() => setShowRecordingComplianceModal(false)}
            aria-hidden
          />
          <div className="relative w-full max-w-md max-h-[85vh] flex flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
            <div className="flex items-start gap-3 border-b border-gray-100 px-5 pt-5 pb-4">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-100">
                <AlertTriangle className="h-5 w-5 text-red-600" />
              </div>
              <div className="min-w-0">
                <h2 id="call-recording-compliance-title" className="text-base font-semibold text-gray-900">
                  Enable call recording & transcription
                </h2>
                <p className="mt-0.5 text-xs text-gray-500">Please review before turning this on</p>
              </div>
            </div>

            <div className="flex-1 space-y-4 overflow-y-auto px-5 py-4 text-[13px] leading-relaxed text-gray-700">
              {/* Mode selection */}
              <div>
                <p className="font-semibold text-gray-900 mb-2">Choose how recordings are handled</p>
                <div className="space-y-2">
                  <button
                    type="button"
                    onClick={() => setPendingRecordingMode('transcribe_only')}
                    className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${
                      pendingRecordingMode === 'transcribe_only'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-200'
                    }`}
                  >
                    <p className="text-[13px] font-semibold text-gray-900">Transcription only</p>
                    <p className="mt-0.5 text-[12px] text-gray-600">
                      Calls are recorded, transcribed, then the audio is <span className="font-medium">automatically
                      deleted from Twilio</span>. You keep the text transcript forever — no audio playback.
                    </p>
                    <p className="mt-1.5 text-[12px] font-medium text-gray-900">
                      Cost: 5,000 AI tokens per recorded minute ($0.05/min equivalent) + Twilio's one-time
                      recording fee (~$0.0025/min) on your Twilio account. <span className="text-green-700">No
                      ongoing storage fees.</span>
                    </p>
                  </button>

                  <button
                    type="button"
                    onClick={() => setPendingRecordingMode('transcribe_store')}
                    className={`w-full rounded-xl border-2 p-3 text-left transition-colors ${
                      pendingRecordingMode === 'transcribe_store'
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 bg-white hover:border-blue-200'
                    }`}
                  >
                    <p className="text-[13px] font-semibold text-gray-900">Transcribe & store</p>
                    <p className="mt-0.5 text-[12px] text-gray-600">
                      Same as above, but the audio stays on Twilio so you can <span className="font-medium">replay
                      calls anytime</span> from the Messages app.
                    </p>
                    <p className="mt-1.5 text-[12px] font-medium text-gray-900">
                      Cost: 5,000 AI tokens per recorded minute ($0.05/min equivalent) + Twilio's recording fee
                      (~$0.0025/min) <span className="text-amber-700">plus ongoing storage (~$0.0005 per minute of
                      audio, per month)</span> on your Twilio account — storage recurs every month until recordings
                      are deleted.
                    </p>
                  </button>
                </div>
                <p className="mt-2 text-[11px] text-gray-500">
                  Transcription tokens are deducted after each call. If your AI token balance runs out,
                  calls simply aren't recorded until you top up — you're never charged beyond your balance.
                </p>
              </div>

              <div>
                <p className="font-semibold text-gray-900">Caller notice</p>
                <p className="mt-1">
                  By default, callers to your business number will hear an automatic announcement:
                  <span className="mt-1 block rounded-lg bg-gray-50 px-3 py-2 italic text-gray-600">
                    "This call may be recorded for quality and record-keeping purposes."
                  </span>
                  <span className="mt-1 block text-[12px] text-gray-500">
                    You can turn this notice off later in settings, but doing so makes your business fully
                    responsible for informing callers that calls are recorded.
                  </span>
                </p>
              </div>

              <div>
                <p className="font-semibold text-gray-900">Your compliance responsibility</p>
                <p className="mt-1">
                  Call recording laws vary by region — some require consent from all parties. By
                  enabling this feature you confirm that you are responsible for complying with the
                  recording and privacy laws that apply to your business and your callers.
                </p>
              </div>
            </div>

            <div className="flex items-center justify-end gap-2 border-t border-gray-100 px-5 py-4">
              <button
                type="button"
                onClick={() => setShowRecordingComplianceModal(false)}
                disabled={callRecordingSaving}
                className="rounded-xl px-4 py-2 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void saveCallRecordingEnabled(true, pendingRecordingMode)}
                disabled={callRecordingSaving}
                className="rounded-xl bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:opacity-60"
              >
                {callRecordingSaving
                  ? 'Saving…'
                  : callRecordingEnabled
                  ? 'Save choice'
                  : 'I agree — enable recording'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body
  );
}
